// components/portal/FloorMapperView.tsx
'use client';

import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { X, MousePointer2, PenTool, Ruler, ZoomIn, ZoomOut, Save, Check, AlertCircle, Maximize2, Minimize2, Layers, ArrowUpRight, Plus, Sparkles, RotateCcw, Search, SlidersHorizontal, Copy, Trash2, Edit3 as EditIcon } from '@/components/ui/lucide-memo';
import { FloorInventoryEntityState, FloorMapperVolatileState, NormalizedPoint, SpatialManifest, LeasingBrandDTO, Pagination, LeasingFloorDTO, LeasingUnitDTO } from '#types/types';
import clsx from 'clsx';
import { toast } from 'sonner';
import { useKernelStore } from '@/kernel/kernelStores';
import { getUnitStatusHex } from './portalTheme';
import cvReadyPromise from '@techstark/opencv-js';

const SNAP_THRESHOLD_PX = 15;
const FILL_DEFAULTS = {
  cannyLow: 25,
  cannyHigh: 80,
  morphKernel: 3,
  morphIterations: 2,
  dilateIterations: 1,
  loDiff: 28,
  upDiff: 28,
  minAreaRatio: 0.0001,
  maxAreaRatio: 0.35,
  approxEpsilonRatio: 0.008,
  blurSize: 3,
};
const FILL_BORDER_MARGIN_PX = 2;
const IMPORT_BATCH_SIZE = 10;

const MemoPlus = React.memo(Plus);
const MemoSparkles = React.memo(Sparkles);
const MemoRotateCcw = React.memo(RotateCcw);
const MemoSearch = React.memo(Search);

const translations = {
  en: {
    distancePrompt: 'Enter the real-world distance between these two points (in meters):',
    calibrationSet: (meters: string) => `Calibration set: Image height = ${meters}m`,
    saveCalibrationFailed: 'Failed to save calibration',
    measureFrontage: 'Measure frontage',
    measureFrontageTitle: 'Measure and set the selected unit frontage from plan',
    frontageNeedsCalibration: 'Calibrate this floor before measuring frontage',
    frontageSelectUnit: 'Select a unit before measuring frontage',
    frontageSet: (meters: string) => `Unit frontage set: ${meters} m`,
    saveFrontageFailed: 'Failed to save frontage',
    applyFrontage: 'Apply frontage',
    creationFailed: 'Creation failed',
    unitCreated: (identifier: string) => `Unit ${identifier} created`,
    promoteAreaSuccess: (area: number) => `Unit area promoted: ${area} m²`,
    promoteAreaFailed: 'Failed to promote area to unit records',
    noUnitsToPromote: 'No units to promote',
    confirmPromoteUnits: (count: number) => `Promote area for ${count} units?`,
    bulkPromoteSuccess: (count: number) => `Successfully promoted ${count} units`,
    bulkPromoteFailed: 'Bulk promotion failed',
    mappingSaved: 'Unit mapping saved',
    saveMappingFailed: 'Failed to save mapping',
    mapperTitle: (floorName: string) => `${floorName} Mapper`,
    propertyLabel: 'Property:',
    selectUnitToMap: 'Select Unit to Map',
    createAndMapUnit: 'Create & Map Unit',
    scaleCalibrated: 'Scale Calibrated',
    imageHeight: 'Image Height',
    surfaceAreaNote:
      'Surface areas are calculated based on this scale. You can recalibrate at any time using the ruler tool.',
    calibrationRequired: 'Calibration Required',
    calibrationHelp:
      'Draw a line of known length to calibrate the scale. This ensures accurate surface area calculations.',
    toggleBlockPlanMode: 'Toggle Block Plan Mode',
    estimatedArea: 'Estimated Area',
    promoteAll: 'Promote All',
    promoteAllTitle: 'Promote all mapped units on this floor',
    promoteToUnit: 'Promote to Unit',
    promoteToUnitTitle: "Push this area to the Unit's official records",
    saveMapping: 'Save Mapping',
    assignBrand: 'Assign Brand',
    reinitializeMapping: 'Reinitialize Mapping',
    brandPickerTitle: 'Assign Brand to Unit',
    brandSearchPlaceholder: 'Search brands...',
    brandSectionMatched: 'Matched to Property Brands',
    brandSectionTableOnly: 'Brands Not In Plan',
    assignBrandLoading: 'Assigning brand...',
    assignBrandSuccess: 'Brand assigned',
    assignBrandFailed: 'Failed to assign brand',
    createBrand: 'Create brand',
    createBrandTitle: 'Create new brand',
    createBrandName: 'Brand name',
    createBrandNamePlaceholder: 'Brand name...',
    createBrandSimilar: 'Similar brands',
    createBrandUseExisting: 'Use existing',
    createBrandSubmit: 'Create & assign',
    createBrandLoading: 'Creating brand...',
    createBrandSuccess: 'Brand created',
    createBrandFailed: 'Failed to create brand',
    createBrandNameRequired: 'Brand name is required',
    reinitMappingLoading: 'Clearing mapping...',
    reinitMappingSuccess: 'Mapping cleared',
    reinitMappingFailed: 'Failed to clear mapping',
    noBrandResults: 'No matching brands',
    close: 'Close',
    renameUnit: 'Rename unit',
    renameUnitPrompt: 'Enter a new unit name:',
    renameUnitSuccess: 'Unit renamed',
    renameUnitFailed: 'Failed to rename unit',
    deleteUnit: 'Remove unit',
    deleteUnitConfirm: 'Remove this unit? This cannot be undone.',
    deleteUnitSuccess: 'Unit removed',
    deleteUnitFailed: 'Failed to remove unit',
    inflateMapping: 'Inflate mapping (+5%)',
    inflateMappingMissing: 'No mapping found to inflate',
    inflateMappingSuccess: 'Mapping inflated',
    inflateMappingFailed: 'Failed to inflate mapping',
    deflateMapping: 'Reduce mapping (-5%)',
    deflateMappingMissing: 'No mapping found to reduce',
    deflateMappingSuccess: 'Mapping reduced',
    deflateMappingFailed: 'Failed to reduce mapping',
    nextLevel: 'Next level',
    prevLevel: 'Previous level',
    fillShape: 'Fill shape',
    fillShapeTitle: 'Auto-detect unit boundary from this point',
    fillShapeLoading: 'Filling shape from plan...',
    fillShapeFailed: 'Failed to detect a valid boundary',
    fillShapeSelectUnit: 'Select a unit to map first',
    fillSettings: 'Fill settings',
    fillSettingsTitle: 'Calibrate auto-fill',
    fillSettingsReset: 'Reset',
    fillSettingsClose: 'Close',
    fillCannyLow: 'Canny low',
    fillCannyHigh: 'Canny high',
    fillLoDiff: 'Flood lo diff',
    fillUpDiff: 'Flood hi diff',
    fillMinArea: 'Min area %',
    fillMaxArea: 'Max area %',
    fillBlurSize: 'Blur size',
    fillMorphIterations: 'Morph iterations',
    fillDilateIterations: 'Dilate iterations',
    importUnits: 'Import units',
    importUnitsTitle: 'Import units from another floor',
    importUnitsEmpty: 'No other floors available',
    importUnitsNone: 'No mapped units on selected floor',
    importUnitsConfirm: (count: number, from: string) => `Import ${count} unit(s) from ${from}? This will create new units without brands.`,
    importUnitsSuccess: (count: number) => `Imported ${count} unit(s)`,
    importUnitsFailed: 'Failed to import units',
  },
  fr: {
    distancePrompt: 'Entrez la distance reelle entre ces deux points (en metres):',
    calibrationSet: (meters: string) => `Calibrage defini: Hauteur image = ${meters}m`,
    saveCalibrationFailed: 'Echec enregistrement calibration',
    measureFrontage: 'Mesurer facade',
    measureFrontageTitle: "Mesurer et definir la facade utile de l'unite selectionnee",
    frontageNeedsCalibration: "Calibrez cet etage avant de mesurer la facade",
    frontageSelectUnit: "Selectionnez une unite avant de mesurer la facade",
    frontageSet: (meters: string) => `Facade unite definie: ${meters} m`,
    saveFrontageFailed: 'Echec enregistrement facade',
    applyFrontage: 'Valider facade',
    creationFailed: 'Creation echouee',
    unitCreated: (identifier: string) => `Unite ${identifier} creee`,
    promoteAreaSuccess: (area: number) => `Surface unite promue: ${area} m²`,
    promoteAreaFailed: 'Echec promotion surface unite',
    noUnitsToPromote: 'Aucune unite a promouvoir',
    confirmPromoteUnits: (count: number) => `Promouvoir la surface pour ${count} unites ?`,
    bulkPromoteSuccess: (count: number) => `Promotion reussie pour ${count} unites`,
    bulkPromoteFailed: 'Echec promotion en masse',
    mappingSaved: 'Mapping unite enregistre',
    saveMappingFailed: 'Echec enregistrement mapping',
    mapperTitle: (floorName: string) => `${floorName} Cartographie`,
    propertyLabel: 'Propriete:',
    selectUnitToMap: 'Selectionner unite a mapper',
    createAndMapUnit: 'Creer & mapper unite',
    scaleCalibrated: 'Echelle calibree',
    imageHeight: 'Hauteur image',
    surfaceAreaNote:
      "Les surfaces sont calculees selon cette echelle. Vous pouvez recalibrer a tout moment avec l'outil regle.",
    calibrationRequired: 'Calibration requise',
    calibrationHelp:
      "Tracez une ligne de longueur connue pour calibrer l'echelle. Cela garantit des surfaces precises.",
    toggleBlockPlanMode: 'Basculer mode plan masse',
    estimatedArea: 'Surface estimee',
    promoteAll: 'Promouvoir tout',
    promoteAllTitle: 'Promouvoir toutes les unites mappees sur cet etage',
    promoteToUnit: 'Promouvoir vers unite',
    promoteToUnitTitle: "Pousser cette surface vers les donnees officielles de l'unite",
    saveMapping: 'Enregistrer mapping',
    assignBrand: 'Assigner marque',
    reinitializeMapping: 'Reinitialiser mapping',
    brandPickerTitle: 'Assigner une marque a l\'unite',
    brandSearchPlaceholder: 'Rechercher des marques...',
    brandSectionMatched: 'Marques liees au plan',
    brandSectionTableOnly: 'Marques hors plan',
    assignBrandLoading: 'Association de la marque...',
    assignBrandSuccess: 'Marque assignee',
    assignBrandFailed: 'Echec assignation marque',
    createBrand: 'Creer une marque',
    createBrandTitle: 'Creer une nouvelle marque',
    createBrandName: 'Nom de la marque',
    createBrandNamePlaceholder: 'Nom de la marque...',
    createBrandSimilar: 'Marques similaires',
    createBrandUseExisting: 'Utiliser',
    createBrandSubmit: 'Creer & associer',
    createBrandLoading: 'Creation de la marque...',
    createBrandSuccess: 'Marque creee',
    createBrandFailed: 'Echec creation marque',
    createBrandNameRequired: 'Nom de la marque requis',
    reinitMappingLoading: 'Suppression du mapping...',
    reinitMappingSuccess: 'Mapping supprime',
    reinitMappingFailed: 'Echec suppression mapping',
    noBrandResults: 'Aucune marque correspondante',
    close: 'Fermer',
    renameUnit: 'Renommer unite',
    renameUnitPrompt: "Entrez un nouveau nom d'unite :",
    renameUnitSuccess: 'Unite renommee',
    renameUnitFailed: 'Echec renommage unite',
    deleteUnit: "Supprimer l'unite",
    deleteUnitConfirm: "Supprimer cette unite ? Cette action est definitive.",
    deleteUnitSuccess: 'Unite supprimee',
    deleteUnitFailed: "Echec suppression unite",
    inflateMapping: 'Agrandir le mapping (+5%)',
    inflateMappingMissing: 'Aucun mapping a agrandir',
    inflateMappingSuccess: 'Mapping agrandi',
    inflateMappingFailed: "Echec de l'agrandissement",
    deflateMapping: 'Reduire le mapping (-5%)',
    deflateMappingMissing: 'Aucun mapping a reduire',
    deflateMappingSuccess: 'Mapping reduit',
    deflateMappingFailed: "Echec de la reduction",
    nextLevel: 'Niveau suivant',
    prevLevel: 'Niveau precedent',
    fillShape: 'Remplir la forme',
    fillShapeTitle: 'Detecter automatiquement la limite depuis ce point',
    fillShapeLoading: 'Remplissage depuis le plan...',
    fillShapeFailed: 'Impossible de detecter une limite valide',
    fillShapeSelectUnit: "Selectionnez d'abord une unite a mapper",
    fillSettings: 'Parametres de remplissage',
    fillSettingsTitle: 'Calibrer auto-remplissage',
    fillSettingsReset: 'Reinitialiser',
    fillSettingsClose: 'Fermer',
    fillCannyLow: 'Canny bas',
    fillCannyHigh: 'Canny haut',
    fillLoDiff: 'Tol. bas',
    fillUpDiff: 'Tol. haut',
    fillMinArea: 'Surface min %',
    fillMaxArea: 'Surface max %',
    fillBlurSize: 'Flou',
    fillMorphIterations: 'Morph iterations',
    fillDilateIterations: 'Dilatation iterations',
    importUnits: 'Importer unites',
    importUnitsTitle: "Importer des unites d'un autre etage",
    importUnitsEmpty: "Aucun autre etage disponible",
    importUnitsNone: "Aucune unite mappee sur l'etage choisi",
    importUnitsConfirm: (count: number, from: string) => `Importer ${count} unite(s) depuis ${from} ? Ceci cree de nouvelles unites sans marques.`,
    importUnitsSuccess: (count: number) => `${count} unite(s) importees`,
    importUnitsFailed: "Echec de l'import",
  },
};

const hexToRgba = (hex: string, alpha: number) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// --- Math Utilities ---

const getAspectRatio = (img: HTMLImageElement) => img.naturalWidth / img.naturalHeight;

const calculateNormalizedDistance = (p1: NormalizedPoint, p2: NormalizedPoint, aspectRatio: number): number => {
  const dx = (p2.x - p1.x) * aspectRatio;
  const dy = p2.y - p1.y;
  return Math.sqrt(dx * dx + dy * dy);
};

const calculateArea = (points: NormalizedPoint[], aspectRatio: number, calibrationFactor: number): number => {
  if (points.length < 3) return 0;
  // Shoelace formula: Points are normalized (0-1). 
  // We project them to physical meters using the calibrationFactor (which is Meters per Image Height).
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const p1 = points[i];
    const p2 = points[(i + 1) % points.length];
    if (!p1 || !p2) continue;
    
    // X is scaled by Aspect Ratio because normalized X=1 is wider than normalized Y=1
    const x1 = p1.x * aspectRatio * calibrationFactor;
    const y1 = p1.y * calibrationFactor;
    const x2 = p2.x * aspectRatio * calibrationFactor;
    const y2 = p2.y * calibrationFactor;
    
    area += (x1 * y2 - x2 * y1);
  }
  return Math.abs(area) / 2;
};

const getCentroid = (points: NormalizedPoint[]): NormalizedPoint => {
  let x = 0, y = 0;
  points.forEach(p => { x += p.x; y += p.y; });
  return { x: x / points.length, y: y / points.length };
};

const inflatePolygon = (points: NormalizedPoint[], factor: number): NormalizedPoint[] => {
  if (points.length === 0) return points;
  const centroid = getCentroid(points);
  const clamp = (val: number) => Math.max(0, Math.min(1, val));
  return points.map(p => ({
    x: clamp(centroid.x + (p.x - centroid.x) * factor),
    y: clamp(centroid.y + (p.y - centroid.y) * factor),
  }));
};

const isNormalizedPoint = (value: unknown): value is NormalizedPoint => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const point = value as { x?: unknown; y?: unknown };
  return typeof point.x === 'number' && Number.isFinite(point.x) && typeof point.y === 'number' && Number.isFinite(point.y);
};

const parseFrontageSegment = (value: unknown): [NormalizedPoint, NormalizedPoint] | null => {
  if (!Array.isArray(value) || value.length !== 2) return null;
  const first = value[0];
  const second = value[1];
  if (!isNormalizedPoint(first) || !isNormalizedPoint(second)) return null;
  return [first, second];
};

const parseFrontageSegments = (value: unknown): Array<[NormalizedPoint, NormalizedPoint]> => {
  if (!Array.isArray(value)) return [];
  return value
    .map((segment) => parseFrontageSegment(segment))
    .filter((segment): segment is [NormalizedPoint, NormalizedPoint] => Boolean(segment));
};

const polylineToSegments = (points: NormalizedPoint[]): Array<[NormalizedPoint, NormalizedPoint]> => {
  if (points.length < 2) return [];
  const segments: Array<[NormalizedPoint, NormalizedPoint]> = [];
  for (let i = 0; i + 1 < points.length; i += 1) {
    const p1 = points[i];
    const p2 = points[i + 1];
    if (!p1 || !p2) continue;
    segments.push([p1, p2]);
  }
  return segments;
};

const calculatePolylineNormalizedDistance = (points: NormalizedPoint[], aspectRatio: number): number => {
  const segments = polylineToSegments(points);
  return segments.reduce((sum, [p1, p2]) => sum + calculateNormalizedDistance(p1, p2, aspectRatio), 0);
};

type PlanImageData = { imageData: ImageData; width: number; height: number };

const decodePlanImageData = async (assetId: string): Promise<PlanImageData | null> => {
  const response = await fetch(`/api/inventory/assets/${assetId}?proxy=1`);
  if (!response.ok) return null;

  const blob = await response.blob();
  const bitmap = await createImageBitmap(blob);
  const width = bitmap.width;
  const height = bitmap.height;
  if (!width || !height) {
    bitmap.close?.();
    return null;
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) {
    bitmap.close?.();
    return null;
  }

  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close?.();
  const imageData = ctx.getImageData(0, 0, width, height);
  return { imageData, width, height };
};

type FillAttemptParams = {
  cannyLow?: number;
  cannyHigh?: number;
  loDiff: number;
  upDiff: number;
  blurSize: number;
  useEdges: boolean;
  morphIterations: number;
  dilateIterations: number;
  morphKernel: number;
  maxAreaRatio: number;
  minAreaRatio: number;
  approxEpsilonRatio: number;
};

type FillAttemptResult = {
  points: NormalizedPoint[];
  area: number;
  valid: boolean;
};

// Pipeline OpenCV pur : aucune dependance au Kernel, au DOM ni a l'etat React.
// Il vit au niveau module (AGENTS 4.3) au lieu d'etre reconstruit a chaque rendu du mapper.
const runFillAttempt = (
  cv: any,
  imageData: ImageData,
  width: number,
  height: number,
  seed: NormalizedPoint,
  params: FillAttemptParams,
): FillAttemptResult | null => {
  let src: any = null;
  let gray: any = null;
  let grayForFill: any = null;
  let edges: any = null;
  let morphKernel: any = null;
  let mask: any = null;
  let maskRoi: any = null;
  let filled: any = null;
  let invertedEdges: any = null;
  let contours: any = null;
  let hierarchy: any = null;
  let approx: any = null;

  try {
    src = cv.matFromImageData(imageData);
    gray = new cv.Mat();
    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);

    if (params.blurSize > 0) {
      const blurKernel = new cv.Size(params.blurSize, params.blurSize);
      cv.GaussianBlur(gray, gray, blurKernel, 0);
    }

    if (params.useEdges) {
      edges = new cv.Mat();
      const cannyLow = params.cannyLow ?? 0;
      const cannyHigh = params.cannyHigh ?? Math.max(1, cannyLow + 20);
      cv.Canny(gray, edges, cannyLow, cannyHigh);
      morphKernel = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(params.morphKernel, params.morphKernel));
      if (params.morphIterations > 0) {
        cv.morphologyEx(edges, edges, cv.MORPH_CLOSE, morphKernel, new cv.Point(-1, -1), params.morphIterations);
      }
      if (params.dilateIterations > 0) {
        cv.dilate(edges, edges, morphKernel, new cv.Point(-1, -1), params.dilateIterations);
      }
    } else {
      edges = cv.Mat.zeros(height, width, cv.CV_8UC1);
    }

    mask = cv.Mat.zeros(height + 2, width + 2, cv.CV_8UC1);
    maskRoi = mask.roi(new cv.Rect(1, 1, width, height));
    edges.copyTo(maskRoi);

    const seedPoint = new cv.Point(Math.round(seed.x * width), Math.round(seed.y * height));
    const rect = new cv.Rect(0, 0, 0, 0);
    const maskOnlyFlag = (cv as any).FLOODFILL_MASK_ONLY ?? 0;
    const flags = 8 | (255 << 8) | maskOnlyFlag;
    grayForFill = gray.clone();
    cv.floodFill(grayForFill, mask, seedPoint, new cv.Scalar(255), rect, new cv.Scalar(params.loDiff), new cv.Scalar(params.upDiff), flags);

    filled = mask.roi(new cv.Rect(1, 1, width, height));
    if (params.useEdges) {
      invertedEdges = new cv.Mat();
      cv.bitwise_not(edges, invertedEdges);
      cv.bitwise_and(filled, invertedEdges, filled);
    } else {
      morphKernel = morphKernel ?? cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(params.morphKernel, params.morphKernel));
      cv.morphologyEx(filled, filled, cv.MORPH_CLOSE, morphKernel, new cv.Point(-1, -1), 1);
    }

    contours = new cv.MatVector();
    hierarchy = new cv.Mat();
    cv.findContours(filled, contours, hierarchy, cv.RETR_EXTERNAL, cv.CHAIN_APPROX_SIMPLE);

    let bestContour: any = null;
    let maxArea = 0;
    for (let i = 0; i < contours.size(); i += 1) {
      const cnt = contours.get(i);
      const area = cv.contourArea(cnt, false);
      const areaRatio = area / (width * height);
      if (areaRatio > params.maxAreaRatio) {
        cnt.delete();
        continue;
      }
      const rectBounds = cv.boundingRect(cnt);
      const touchesBorder = rectBounds.x <= FILL_BORDER_MARGIN_PX
        || rectBounds.y <= FILL_BORDER_MARGIN_PX
        || rectBounds.x + rectBounds.width >= width - FILL_BORDER_MARGIN_PX
        || rectBounds.y + rectBounds.height >= height - FILL_BORDER_MARGIN_PX;
      if (touchesBorder && areaRatio > params.maxAreaRatio * 0.6) {
        cnt.delete();
        continue;
      }
      const inSeed = cv.pointPolygonTest(cnt, seedPoint, false) >= 0;
      if (!inSeed) {
        cnt.delete();
        continue;
      }
      if (area > maxArea) {
        if (bestContour) {
          bestContour.delete();
        }
        bestContour = cnt.clone();
        maxArea = area;
      }
      cnt.delete();
    }

    const minArea = Math.max(60, width * height * params.minAreaRatio);
    if (!bestContour || maxArea < minArea) {
      if (bestContour) bestContour.delete();
      return { points: [], area: maxArea, valid: false };
    }

    approx = new cv.Mat();
    const perimeter = cv.arcLength(bestContour, true);
    const epsilon = Math.max(1, perimeter * params.approxEpsilonRatio);
    cv.approxPolyDP(bestContour, approx, epsilon, true);
    bestContour.delete();

    const points: NormalizedPoint[] = [];
    const data = approx.data32S;
    for (let i = 0; i < data.length; i += 2) {
      points.push({ x: data[i] / width, y: data[i + 1] / height });
    }

    if (points.length < 3) {
      return { points: [], area: maxArea, valid: false };
    }
    return { points, area: maxArea, valid: true };
  } finally {
    if (approx) approx.delete();
    if (hierarchy) hierarchy.delete();
    if (contours) contours.delete();
    if (invertedEdges) invertedEdges.delete();
    if (filled) filled.delete();
    if (maskRoi) maskRoi.delete();
    if (mask) mask.delete();
    if (morphKernel) morphKernel.delete();
    if (edges) edges.delete();
    if (grayForFill) grayForFill.delete();
    if (gray) gray.delete();
    if (src) src.delete();
  }
};

const extractPolygonFromImageData = async (
  seed: NormalizedPoint,
  imageData: ImageData,
  width: number,
  height: number,
  fillSettings: typeof FILL_DEFAULTS,
): Promise<NormalizedPoint[] | null> => {
  const cv = await cvReadyPromise;
  const attempts: FillAttemptParams[] = [
    {
      loDiff: fillSettings.loDiff,
      upDiff: fillSettings.upDiff,
      blurSize: fillSettings.blurSize,
      useEdges: true,
      morphIterations: fillSettings.morphIterations,
      dilateIterations: fillSettings.dilateIterations,
      cannyLow: fillSettings.cannyLow,
      cannyHigh: fillSettings.cannyHigh,
      minAreaRatio: fillSettings.minAreaRatio,
      maxAreaRatio: fillSettings.maxAreaRatio,
      approxEpsilonRatio: fillSettings.approxEpsilonRatio,
      morphKernel: fillSettings.morphKernel,
    },
    {
      loDiff: fillSettings.loDiff * 2,
      upDiff: fillSettings.upDiff * 2,
      blurSize: Math.max(3, fillSettings.blurSize + 2),
      useEdges: true,
      morphIterations: fillSettings.morphIterations + 1,
      dilateIterations: fillSettings.dilateIterations + 1,
      cannyLow: Math.max(5, Math.floor(fillSettings.cannyLow / 2)),
      cannyHigh: Math.max(25, Math.floor(fillSettings.cannyHigh / 2)),
      minAreaRatio: fillSettings.minAreaRatio,
      maxAreaRatio: fillSettings.maxAreaRatio,
      approxEpsilonRatio: fillSettings.approxEpsilonRatio,
      morphKernel: fillSettings.morphKernel,
    },
    {
      loDiff: fillSettings.loDiff * 2,
      upDiff: fillSettings.upDiff * 2,
      blurSize: Math.max(3, fillSettings.blurSize),
      useEdges: false,
      morphIterations: 1,
      dilateIterations: 0,
      minAreaRatio: fillSettings.minAreaRatio,
      maxAreaRatio: Math.min(0.35, fillSettings.maxAreaRatio),
      approxEpsilonRatio: fillSettings.approxEpsilonRatio,
      morphKernel: fillSettings.morphKernel,
    },
  ];

  let best: { points: NormalizedPoint[]; area: number } | null = null;
  for (const attempt of attempts) {
    const result = runFillAttempt(cv, imageData, width, height, seed, attempt);
    if (!result?.valid) continue;
    if (!best || result.area > best.area) {
      best = { points: result.points, area: result.area };
    }
  }

  return best?.points ?? null;
};

const normalizeBrandName = (value: string): string => {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
};

const levenshteinDistance = (a: string, b: string): number => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const matrix: number[][] = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
  for (let i = 0; i <= a.length; i += 1) matrix[i]![0] = i;
  for (let j = 0; j <= b.length; j += 1) matrix[0]![j] = j;
  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      const row = matrix[i]!;
      const prevRow = matrix[i - 1]!;
      row[j] = Math.min(
        prevRow[j]! + 1,
        row[j - 1]! + 1,
        prevRow[j - 1]! + cost
      );
    }
  }
  return matrix[a.length]![b.length] ?? 0;
};

const scoreBrandSimilarity = (query: string, candidate: string): number => {
  if (!query || !candidate) return 0;
  if (query === candidate) return 1;
  if (candidate.includes(query) || query.includes(candidate)) return 0.92;
  const distance = levenshteinDistance(query, candidate);
  const maxLen = Math.max(query.length, candidate.length);
  const ratio = maxLen ? 1 - distance / maxLen : 0;
  const queryTokens = query.split(' ').filter(Boolean);
  const tokenMatches = queryTokens.length
    ? queryTokens.filter(token => candidate.includes(token)).length / queryTokens.length
    : 0;
  return Math.max(0, ratio * 0.7 + tokenMatches * 0.3);
};

// --- Component ---

interface FloorMapperViewProps {
  floorId: string;
}

const defaultMapperState: FloorMapperVolatileState = {
  viewport: { x: 0, y: 0, zoom: 1 },
  interactionMode: 'IDLE',
  activeUnitId: null,
  draftPoints: [],
  calibrationPoints: null,
  frontagePoints: [],
};

const parseFloorMapperState = (value: unknown): FloorMapperVolatileState | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const state = value as Record<string, unknown>;
  const viewportValue = state.viewport as Record<string, unknown> | undefined;
  const mode = state.interactionMode;

  const interactionMode =
    mode === 'IDLE' ||
    mode === 'DRAWING_NEW' ||
    mode === 'EDITING_POINT' ||
    mode === 'CALIBRATING' ||
    mode === 'MEASURE_FRONTAGE' ||
    mode === 'PANNING'
      ? mode
      : 'IDLE';

  const viewport = {
    x: typeof viewportValue?.x === 'number' && Number.isFinite(viewportValue.x) ? viewportValue.x : 0,
    y: typeof viewportValue?.y === 'number' && Number.isFinite(viewportValue.y) ? viewportValue.y : 0,
    zoom: typeof viewportValue?.zoom === 'number' && Number.isFinite(viewportValue.zoom) ? viewportValue.zoom : 1,
  };

  const draftPoints = Array.isArray(state.draftPoints)
    ? (state.draftPoints.filter(
        (point): point is NormalizedPoint =>
          !!point &&
          typeof point === 'object' &&
          !Array.isArray(point) &&
          typeof (point as { x?: unknown }).x === 'number' &&
          typeof (point as { y?: unknown }).y === 'number'
      ) as NormalizedPoint[])
    : [];

  const calibrationPoints = Array.isArray(state.calibrationPoints)
    ? (state.calibrationPoints as unknown[]).slice(0, 2).filter(
        (point): point is NormalizedPoint =>
          !!point &&
          typeof point === 'object' &&
          !Array.isArray(point) &&
          typeof (point as { x?: unknown }).x === 'number' &&
          typeof (point as { y?: unknown }).y === 'number'
      )
    : [];

  const calibrationPair =
    calibrationPoints.length === 2 && calibrationPoints[0] && calibrationPoints[1]
      ? [calibrationPoints[0], calibrationPoints[1]] as [NormalizedPoint, NormalizedPoint]
      : null;

  const frontagePoints = Array.isArray(state.frontagePoints)
    ? (state.frontagePoints.filter(
        (point): point is NormalizedPoint =>
          !!point &&
          typeof point === 'object' &&
          !Array.isArray(point) &&
          typeof (point as { x?: unknown }).x === 'number' &&
          typeof (point as { y?: unknown }).y === 'number'
      ) as NormalizedPoint[])
    : [];

  return {
    viewport,
    interactionMode,
    activeUnitId: typeof state.activeUnitId === 'string' ? state.activeUnitId : null,
    draftPoints,
    calibrationPoints: calibrationPair,
    frontagePoints,
  };
};

export const FloorMapperView = ({ floorId }: FloorMapperViewProps) => {
  const { kernel } = useKernel();
  const lang = useKernelStore(state => state.language);
  const t = translations[lang] || translations.en;
  const floorState = useKernelStore(
    (state) => (state.inventory.Floor.registry[floorId] as FloorInventoryEntityState | undefined) ?? null
  );

  const mapperState = useMemo<FloorMapperVolatileState>(() => {
    const runtimeMapperState = floorState?.mapperState ?? floorState?.runtime?.mapperState;
    const parsed = parseFloorMapperState(runtimeMapperState);
    return parsed ?? defaultMapperState;
  }, [floorState]);

  const [draggingVertexIndex, setDraggingVertexIndex] = useState<number | null>(null);
  const [snappedPoint, setSnappedPoint] = useState<NormalizedPoint | null>(null);
  const [isSnapped, setIsSnapped] = useState(false);
  // Miroirs des trois etats haute frequence (souris) : les callbacks les lisent a l'execution
  // au lieu de les capturer en dependance. Synchronises au moment de l'ecriture (setMapperCompat).
  const draggingVertexIndexRef = useRef<number | null>(null);
  const snappedPointRef = useRef<NormalizedPoint | null>(null);
  const isSnappedRef = useRef(false);
  const viewportRef = useRef(mapperState.viewport);

  const updateMapperState = useCallback(
    (
      patch:
        | Partial<FloorMapperVolatileState>
        | ((prev: FloorMapperVolatileState) => Partial<FloorMapperVolatileState>)
    ) => {
      const k = kernel.current;
      if (!k) return;
      const current = parseFloorMapperState(k.readEntityRuntimeState('Floor', floorId).mapperState) ?? defaultMapperState;
      const resolvedPatch = typeof patch === 'function' ? patch(current) : patch;
      k.patchEntityRuntimeState('Floor', floorId, {
        mapperState: {
          ...current,
          ...resolvedPatch,
        },
      });
    },
    [floorId, kernel]
  );

  // Lecture fraiche de l'etat mapper persiste au moment de l'execution du callback.
  const readMapper = useCallback(
    (): FloorMapperVolatileState =>
      parseFloorMapperState(kernel.current?.readEntityRuntimeState('Floor', floorId).mapperState) ?? defaultMapperState,
    [floorId, kernel]
  );

  useEffect(() => {
    viewportRef.current = mapperState.viewport;
  }, [mapperState.viewport]);

  const applyViewportChange = useCallback((newViewport: { x: number; y: number; zoom: number }) => {
    viewportRef.current = newViewport;
    updateMapperState({ viewport: newViewport });
  }, [updateMapperState]);

  const mapper = useMemo(
    () => ({
      ...mapperState,
      floorId,
      state: mapperState.interactionMode,
      draggingVertexIndex,
      snappedPoint,
      isSnapped,
    }),
    [mapperState, floorId, draggingVertexIndex, snappedPoint, isSnapped]
  );

  const setMapperCompat = useCallback(
    (
      updater:
        | (typeof mapper)
        | ((prev: typeof mapper) => typeof mapper)
    ) => {
      const currentState = readMapper();
      const prev = {
        ...currentState,
        floorId,
        state: currentState.interactionMode,
        draggingVertexIndex: draggingVertexIndexRef.current,
        snappedPoint: snappedPointRef.current,
        isSnapped: isSnappedRef.current,
      };
      const next = typeof updater === 'function' ? updater(prev) : updater;
      const resolved = next as typeof mapper;

      updateMapperState({
        viewport: resolved.viewport ?? viewportRef.current,
        interactionMode: resolved.state ?? resolved.interactionMode,
        activeUnitId: resolved.activeUnitId ?? null,
        draftPoints: resolved.draftPoints ?? [],
        calibrationPoints: resolved.calibrationPoints ?? null,
        frontagePoints: resolved.frontagePoints ?? [],
      });

      draggingVertexIndexRef.current = resolved.draggingVertexIndex ?? null;
      snappedPointRef.current = resolved.snappedPoint ?? null;
      isSnappedRef.current = Boolean(resolved.isSnapped);
      setDraggingVertexIndex(draggingVertexIndexRef.current);
      setSnappedPoint(snappedPointRef.current);
      setIsSnapped(isSnappedRef.current);
    },
    [floorId, readMapper, updateMapperState]
  );

  const floorRegistry = useKernelStore(state => state.inventory.Floor.registry);
  const unitRegistry = useKernelStore(state => state.inventory.Unit.registry);
  const propertyRegistry = useKernelStore(state => state.inventory.Property.registry);
  const dealRegistry = useKernelStore(state => state.inventory.Deal.registry);
  const brandRegistry = useKernelStore(state => state.inventory.Brand.registry);

  useEffect(() => {
    const floorEntry = floorRegistry[floorId];
    // IMPORTANT: un Floor peut exister en mode summary (sans unitIds).
    // Le mapper a besoin du détail pour hydrater toutes les unités concernées.
    if (!floorEntry || !Array.isArray(floorEntry.unitIds)) {
      void kernel.current?.syncEntity('Floor', floorId);
    }
  }, [floorId, floorRegistry, kernel]);

  const floor = useMemo(() => floorRegistry[floorId] ?? null, [floorId, floorRegistry]);

  const property = useMemo(() =>
    floor ? propertyRegistry[floor.propertyId] : null
  , [floor, propertyRegistry]);

  const scopedUnitIdsForHydration = useMemo(() => {
    if (Array.isArray(floor?.unitIds) && floor.unitIds.length > 0) return floor.unitIds;
    if (Array.isArray(property?.unitIds) && property.unitIds.length > 0) return property.unitIds;
    return [] as string[];
  }, [floor?.unitIds, property?.unitIds]);

  useEffect(() => {
    const k = kernel.current;
    if (!k || scopedUnitIdsForHydration.length === 0) return;

    void k.ensureInventoryIds('Unit', scopedUnitIdsForHydration, { force: false, batchSize: 200 });
  }, [kernel, scopedUnitIdsForHydration]);

  const floorUnits = useMemo(() => {
    return Object.values(unitRegistry).filter(u =>
      u.floorIds?.includes(floorId)
    );
  }, [floorId, unitRegistry]);

  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const planImageDataRef = useRef<{ assetId: string; promise: Promise<PlanImageData | null> } | null>(null);

  const activeFloorUnit = useMemo(
    () => (mapper.activeUnitId ? unitRegistry[mapper.activeUnitId] ?? null : null),
    [mapper.activeUnitId, unitRegistry]
  );

  const activeFrontageSegments = useMemo<Array<[NormalizedPoint, NormalizedPoint]>>(() => {
    if (!activeFloorUnit || !mapper.floorId) return [];
    const unitPosition = activeFloorUnit.positions?.find((position) => position.floorId === mapper.floorId);
    const meta = (unitPosition?.geoJson as any)?.meta;
    const multiSegments = parseFrontageSegments(meta?.frontageSegments);
    if (multiSegments.length > 0) return multiSegments;
    const legacySingle = parseFrontageSegment(meta?.frontageSegment);
    return legacySingle ? [legacySingle] : [];
  }, [activeFloorUnit, mapper.floorId]);

  const frontageSegmentsToRender = useMemo<Array<[NormalizedPoint, NormalizedPoint]>>(() => {
    if (mapper.state === 'MEASURE_FRONTAGE') {
      return polylineToSegments(mapper.frontagePoints);
    }
    return activeFrontageSegments;
  }, [activeFrontageSegments, mapper.frontagePoints, mapper.state]);

  // eslint-disable-next-line react-hooks/refs -- pre-existing pattern in this file; the plan's natural size is only known once the image has loaded, and every reader is gated behind imgLoaded, which the plan change resets
  const measuredFrontagePreview = useMemo(() => {
    if (!imgRef.current || !floor?.calibrationFactor) return 0;

    const previewPoints =
      mapper.state === 'MEASURE_FRONTAGE' && mapper.snappedPoint && mapper.frontagePoints.length > 0
        ? [...mapper.frontagePoints, mapper.snappedPoint]
        : mapper.frontagePoints;

    if (previewPoints.length < 2) return 0;

    const normalizedDistance = calculatePolylineNormalizedDistance(previewPoints, getAspectRatio(imgRef.current));
    return normalizedDistance * floor.calibrationFactor;
  }, [floor?.calibrationFactor, mapper.frontagePoints, mapper.snappedPoint, mapper.state]);

  const frontagePointsToRender = useMemo<NormalizedPoint[]>(() => {
    if (mapper.state === 'MEASURE_FRONTAGE') return mapper.frontagePoints;
    return frontageSegmentsToRender.flatMap(([p1, p2]) => [p1, p2]);
  }, [frontageSegmentsToRender, mapper.frontagePoints, mapper.state]);

  const unitOccupants = useMemo(() => {
    const map: Record<string, { name: string | null; logo: string | null }> = {};
    floorUnits.forEach(u => {
      const deal = u.activeDealId ? dealRegistry[u.activeDealId] : null;
      const brand = deal?.brandId ? brandRegistry[deal.brandId] : null;
      map[u.id] = {
        name: brand?.name || deal?.tenantLegalName || null,
        logo: brand?.logoUrl || null
      };
    });
    return map;
  }, [floorUnits, dealRegistry, brandRegistry]);

  const importFloors = useMemo(() => {
    if (!floor || !property?.floorIds) return [];
    return property.floorIds
      .map(id => floorRegistry[id])
      .filter(f => f && f.id !== floor.id) as LeasingFloorDTO[];
  }, [floor, property, floorRegistry]);

  const [opacityMode, setOpacityMode] = useState<'TRANSPARENT' | 'OPAQUE'>('OPAQUE');
  const [imgLoaded, setImgLoaded] = useState(false);
  const [isFillingShape, setIsFillingShape] = useState(false);
  const [isFillSettingsOpen, setIsFillSettingsOpen] = useState(false);
  const [fillSettings, setFillSettings] = useState({ ...FILL_DEFAULTS });
  const [isImportUnitsOpen, setIsImportUnitsOpen] = useState(false);
  const [isImportingUnits, setIsImportingUnits] = useState(false);
  const [isBrandPickerOpen, setIsBrandPickerOpen] = useState(false);
  const [brandPickerUnitId, setBrandPickerUnitId] = useState<string | null>(null);
  const [brandSearch, setBrandSearch] = useState('');
  const [isCreateBrandOpen, setIsCreateBrandOpen] = useState(false);
  const [newBrandName, setNewBrandName] = useState('');
  const [isCreatingBrand, setIsCreatingBrand] = useState(false);
  const [failedBrandLogos, setFailedBrandLogos] = useState<Set<string>>(() => new Set());
  const [brandQueryIds, setBrandQueryIds] = useState<string[]>([]);
  const [brandQueryPagination, setBrandQueryPagination] = useState<Pagination<'Brand'> | null>(null);
  const [isBrandQueryLoading, setIsBrandQueryLoading] = useState(false);
  const [allBrandIds, setAllBrandIds] = useState<string[]>([]);
  const brandListRef = useRef<HTMLDivElement>(null);
  const brandLoadMoreRef = useRef<HTMLDivElement>(null);
  const brandPickerOpenRef = useRef(false);
  const hasLoadedAllBrandsRef = useRef(false);
  const brandQuerySeqRef = useRef(0);
  const brandQuerySearchRef = useRef('');

  const updateFillSetting = useCallback(<K extends keyof typeof FILL_DEFAULTS>(key: K, value: (typeof FILL_DEFAULTS)[K]) => {
    setFillSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  const markBrandLogoFailed = useCallback((url?: string | null) => {
    if (!url) return;
    setFailedBrandLogos(prev => {
      if (prev.has(url)) return prev;
      const next = new Set(prev);
      next.add(url);
      return next;
    });
  }, []);

  const isDraggingRef = useRef(false);
  const lastMousePosRef = useRef({ x: 0, y: 0 });

  const adjustZoom = useCallback((delta: number) => {
    const currentZoom = viewportRef.current.zoom;
    const zoomMultiplier = delta > 0 ? 1.4 : (1 / 1.4);
    const newZoom = Math.min(15, Math.max(0.1, currentZoom * zoomMultiplier));

    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const mouseX = rect.width / 2;
    const mouseY = rect.height / 2;

    const currentPan = { x: viewportRef.current.x, y: viewportRef.current.y };
    const newPanX = mouseX - (mouseX - currentPan.x) * (newZoom / currentZoom);
    const newPanY = mouseY - (mouseY - currentPan.y) * (newZoom / currentZoom);

    applyViewportChange({ x: newPanX, y: newPanY, zoom: newZoom });
  }, [applyViewportChange]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleNativeWheel = (e: WheelEvent) => {
      e.preventDefault();

      const rect = container.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const microZoomSensitivity = 0.005;
      const zoomMultiplier = Math.exp(-e.deltaY * microZoomSensitivity);

      const currentZoom = viewportRef.current.zoom;
      const currentPan = { x: viewportRef.current.x, y: viewportRef.current.y };

      const newZoom = Math.min(15, Math.max(0.1, currentZoom * zoomMultiplier));

      const newPanX = mouseX - (mouseX - currentPan.x) * (newZoom / currentZoom);
      const newPanY = mouseY - (mouseY - currentPan.y) * (newZoom / currentZoom);

      applyViewportChange({ x: newPanX, y: newPanY, zoom: newZoom });
    };

    container.addEventListener('wheel', handleNativeWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleNativeWheel);
  }, [applyViewportChange]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; retracting the loaded flag when the plan changes IS this effect's purpose
    setImgLoaded(false);
    // Libere les pixels du plan precedent (plusieurs Mo) des que le plan change.
    planImageDataRef.current = null;
  }, [floor?.planFileId]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const activeElement = document.activeElement as HTMLElement | null;
      const isTypingTarget =
        activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA' ||
        activeElement?.isContentEditable;
      if (isTypingTarget) return;

      if (event.key === 'Escape') {
        const current = readMapper();
        if (current.interactionMode === 'MEASURE_FRONTAGE' && current.frontagePoints.length > 0) {
          event.preventDefault();
          setMapperCompat(prev => ({ ...prev, frontagePoints: prev.frontagePoints.slice(0, -1) }));
          return;
        }

        if (current.interactionMode === 'CALIBRATING' && current.calibrationPoints) {
          event.preventDefault();
          setMapperCompat(prev => ({ ...prev, calibrationPoints: null }));
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [readMapper, setMapperCompat]);

  const hydrateAllBrands = useCallback(async () => {
    if (!kernel.current || hasLoadedAllBrandsRef.current) return;
    hasLoadedAllBrandsRef.current = true;
    const collected: string[] = [];
    const seen = new Set<string>();
    let offset = 0;
    const limit = 500;
    let pageCount = 0;
    const MAX_PAGES = 50;

    while (pageCount < MAX_PAGES) {
      const result = await kernel.current.queryInventory('Brand', { filters: { includeLiquidated: true }, limit, offset });
      result.ids.forEach((id) => {
        if (seen.has(id)) return;
        seen.add(id);
        collected.push(id);
      });
      if (!result.pagination?.hasMore) break;
      offset += result.pagination.limit ?? limit;
      pageCount += 1;
    }

    setAllBrandIds(collected);
  }, [kernel]);

  useEffect(() => {
    if (!mapper.floorId) return;
    void hydrateAllBrands();
  }, [mapper.floorId, hydrateAllBrands]);

  const scrapedBrandNames = useMemo(() => {
    const data = property?.scrapedData;
    let parsed: any = data;
    if (typeof data === 'string') {
      try {
        parsed = JSON.parse(data);
      } catch {
        parsed = null;
      }
    }
    const directNames = parsed?.brandNames;
    const profileNames = parsed?.profile?.brandNames;
    const names = Array.isArray(profileNames) ? profileNames : directNames;
    if (!Array.isArray(names)) return [];
    const normalized = new Set<string>();
    const result: string[] = [];
    names.forEach((name: any) => {
      if (typeof name !== 'string') return;
      const clean = name.trim();
      if (!clean) return;
      const key = clean.toLowerCase();
      if (normalized.has(key)) return;
      normalized.add(key);
      result.push(clean);
    });
    return result;
  }, [property?.scrapedData]);


  // Brands of the property's scraped plan, matched on exact case-insensitive name
  // against the Brand registry already hydrated by hydrateAllBrands.
  const matchedPlanBrands = useMemo(() => {
    if (!isBrandPickerOpen || scrapedBrandNames.length === 0) return [];
    const wanted = new Set(scrapedBrandNames.map(name => name.trim().toLowerCase()));
    const sourceIds = allBrandIds.length > 0 ? allBrandIds : Object.keys(brandRegistry);
    return sourceIds
      .map(id => brandRegistry[id])
      .filter((brand): brand is LeasingBrandDTO => !!brand && wanted.has((brand.name ?? '').trim().toLowerCase()))
      .map(brand => ({ id: brand.id, name: brand.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [isBrandPickerOpen, scrapedBrandNames, allBrandIds, brandRegistry]);

  const matchedPlanBrandIds = useMemo(() => new Set(matchedPlanBrands.map(b => b.id)), [matchedPlanBrands]);

  const queryBrandsForPicker = useCallback(async (searchInput: string, append = false) => {
    if (!kernel.current) return;
    const search = searchInput.trim();
    if (!append) {
      setIsBrandQueryLoading(true);
    }
    const nextSeq = brandQuerySeqRef.current + 1;
    brandQuerySeqRef.current = nextSeq;
    try {
      const offset = append ? ((brandQueryPagination?.offset ?? 0) + (brandQueryPagination?.limit ?? 100)) : 0;
      const result = await kernel.current.queryInventory('Brand', {
        filters: { search },
        limit: brandQueryPagination?.limit ?? 100,
        offset,
      });

      // Drop stale async responses when search changed quickly.
      if (brandQuerySeqRef.current !== nextSeq) return;

      brandQuerySearchRef.current = search;
      setBrandQueryPagination(result.pagination as Pagination<'Brand'> | null);
      setBrandQueryIds((prev) => {
        if (!append) return result.ids;
        const seen = new Set(prev);
        const merged = [...prev];
        result.ids.forEach((id) => {
          if (seen.has(id)) return;
          seen.add(id);
          merged.push(id);
        });
        return merged;
      });
    } finally {
      if (brandQuerySeqRef.current === nextSeq) {
        setIsBrandQueryLoading(false);
      }
    }
  }, [kernel, brandQueryPagination?.limit, brandQueryPagination?.offset]);

  const brandPickerSections = useMemo(() => {
    const normalize = (value: string) => value.trim().toLowerCase();
    const scrapedMap = new Map<string, string>();
    scrapedBrandNames.forEach(name => {
      const key = normalize(name);
      if (!scrapedMap.has(key)) scrapedMap.set(key, name);
    });

    const tableBrands = brandQueryIds
      .map(id => brandRegistry[id])
      .filter((brand): brand is LeasingBrandDTO => Boolean(brand));

    const tableOnly: typeof tableBrands = [];

    tableBrands.forEach(brand => {
      if (!matchedPlanBrandIds.has(brand.id)) {
        tableOnly.push(brand);
      }
    });

    const query = brandSearch.trim().toLowerCase();
    const matchesQuery = (name: string) => (query.length === 0 ? true : name.toLowerCase().includes(query));

    return {
      matchedInTable: matchedPlanBrands.filter(b => matchesQuery(b.name)),
      tableOnly: tableOnly.filter(b => matchesQuery(b.name)),
    };
  }, [brandQueryIds, brandRegistry, scrapedBrandNames, brandSearch, matchedPlanBrandIds, matchedPlanBrands]);

  const similarBrandMatches = useMemo(() => {
    const query = normalizeBrandName(newBrandName);
    if (!query) return [];
    const sourceIds = allBrandIds.length > 0 ? allBrandIds : Object.keys(brandRegistry);
    const matches = sourceIds
      .map(id => brandRegistry[id])
      .filter((brand): brand is LeasingBrandDTO => Boolean(brand))
      .map(brand => {
        const normalized = normalizeBrandName(brand.name ?? '');
        const score = scoreBrandSimilarity(query, normalized);
        return { id: brand.id, name: brand.name, score };
      })
      .filter(item => item.score >= 0.55)
      .sort((a, b) => b.score - a.score)
      .slice(0, 6);
    return matches;
  }, [allBrandIds, brandRegistry, newBrandName]);

  const handlePromoteArea = useCallback(async (unitId?: string) => {
    const k = kernel.current;
    if (!k) return;

    const targetUnitId = unitId ?? mapper.activeUnitId;
    if (!targetUnitId || !imgRef.current || !mapper.floorId) return;

    const inv = k.getInventory();
    const currentFloor = inv.Floor.registry[mapper.floorId];
    const activeUnit = inv.Unit.registry[targetUnitId];
    if (!currentFloor || !activeUnit) return;

    const aspectRatio = getAspectRatio(imgRef.current);
    const calibration = currentFloor.calibrationFactor || 1.0;
    const currentArea = calculateArea(mapper.draftPoints, aspectRatio, calibration);

    // Sum areas from other floors to support multi-level units
    let totalArea = currentArea;
    activeUnit.positions?.forEach(pos => {
      // Skip the current floor as we are using the 'currentArea' from the active draft
      if (pos.floorId !== mapper.floorId && pos.geoJson?.meta?.calculatedAreaSqm) {
        totalArea += pos.geoJson.meta.calculatedAreaSqm;
      }
    });

    try {
      await k.mutateInventory([{
        action: 'UPDATE',
        entity: 'Unit',
        id: targetUnitId,
        payload: { areaSqm: Math.round(totalArea) }
      }], 'Promote mapped area to unit');
      toast.success(t.promoteAreaSuccess(Math.round(totalArea)));
      void k.refreshInventory('Unit');
    } catch (e) {
      toast.error(t.promoteAreaFailed);
    }
  }, [kernel, mapper.activeUnitId, mapper.floorId, mapper.draftPoints, t]);

  const handleAssignBrand = useCallback(async (unitId: string, brandId: string) => {
    const k = kernel.current;
    if (!k) return;
    const toastId = toast.loading(t.assignBrandLoading);
    try {
      await k.mutateInventory([{
        action: 'CREATE',
        entity: 'Deal',
        payload: {
          unitId,
          brandId,
          status: 'SIGNED',
          financials: { rent: { amount: 0, currency: 'EUR', basis: 'YEARLY' } },
          activateOnSign: true
        }
      }], 'Assign brand to unit');
      toast.success(t.assignBrandSuccess);
      void k.refreshInventory('Unit');
    } catch (error) {
      console.error('[Plan Mapper] Assign brand failed', error);
      toast.error(t.assignBrandFailed);
    } finally {
      toast.dismiss(toastId);
    }
  }, [kernel, t]);

  const handleReinitializeMapping = useCallback(async (unitId: string) => {
    if (!floor?.id) return;
    const k = kernel.current;
    if (!k) return;
    const toastId = toast.loading(t.reinitMappingLoading);
    try {
      await k.mutateInventory([{
        action: 'UPDATE',
        entity: 'UnitPosition',
        payload: { unitId, floorId: floor.id, geoJson: null }
      }], 'Reset unit mapping');

      setMapperCompat(prev => ({
        ...prev,
        draftPoints: prev.activeUnitId === unitId ? [] : prev.draftPoints
      }));

      toast.success(t.reinitMappingSuccess);
    } catch (error) {
      console.error('[Plan Mapper] Reinitialize mapping failed', error);
      toast.error(t.reinitMappingFailed);
    } finally {
      toast.dismiss(toastId);
    }
  }, [floor?.id, kernel, t]);

  const handleRenameUnit = useCallback(async (unitId: string) => {
    const k = kernel.current;
    if (!k) return;
    const unit = k.getInventory().Unit.registry[unitId];
    if (!unit) return;
    const currentName = unit.unitIdentifier ?? unit.name ?? '';
    const nextName = window.prompt(t.renameUnitPrompt, currentName);
    if (!nextName || !nextName.trim() || nextName.trim() === currentName) return;

    try {
      await k.mutateInventory([{
        action: 'UPDATE',
        entity: 'Unit',
        id: unitId,
        payload: { unitIdentifier: nextName.trim() }
      }], 'Rename unit');
      toast.success(t.renameUnitSuccess);
      void k.refreshInventory('Unit');
    } catch (error) {
      console.error('[Plan Mapper] Rename unit failed', error);
      toast.error(t.renameUnitFailed);
    }
  }, [kernel, t]);

  const handleDeleteUnit = useCallback(async (unitId: string) => {
    const k = kernel.current;
    if (!k) return;
    if (!window.confirm(t.deleteUnitConfirm)) return;

    try {
      await k.mutateInventory([{
        action: 'DELETE',
        entity: 'Unit',
        id: unitId
      }], 'Delete unit');
      toast.success(t.deleteUnitSuccess);
      if (mapper.activeUnitId === unitId) {
        setMapperCompat(prev => ({ ...prev, activeUnitId: null, draftPoints: [], frontagePoints: [] }));
      }
      void k.refreshInventory('Unit');
    } catch (error) {
      console.error('[Plan Mapper] Delete unit failed', error);
      toast.error(t.deleteUnitFailed);
    }
  }, [kernel, mapper.activeUnitId, t]);


  const handleOpenBrandPicker = useCallback((unitId: string) => {
    setBrandPickerUnitId(unitId);
    setBrandSearch('');
    setIsBrandPickerOpen(true);
  }, [kernel]);

  const handleCloseBrandPicker = useCallback(() => {
    setIsBrandPickerOpen(false);
    setBrandPickerUnitId(null);
    setBrandSearch('');
    setIsCreateBrandOpen(false);
    setNewBrandName('');
    setIsCreatingBrand(false);
  }, []);

  const handleOpenCreateBrand = useCallback(() => {
    setNewBrandName(brandSearch.trim());
    setIsCreateBrandOpen(true);
  }, [brandSearch, kernel]);

  const handleCloseCreateBrand = useCallback(() => {
    setIsCreateBrandOpen(false);
    setNewBrandName('');
    setIsCreatingBrand(false);
  }, []);

  const handleCreateBrand = useCallback(async () => {
    if (!brandPickerUnitId) return;
    const name = newBrandName.trim();
    if (!name) {
      toast.error(t.createBrandNameRequired);
      return;
    }
    if (isCreatingBrand) return;
    setIsCreatingBrand(true);
    const toastId = toast.loading(t.createBrandLoading);

    try {
      const k = kernel.current;
      if (!k) return;
      const tempId = `brand-${Date.now()}`;
      await k.mutateInventory([
        {
          action: 'CREATE',
          entity: 'Brand',
          tempId,
          payload: {
            name,
            expansionTeamContacts: [],
            keyContacts: []
          }
        },
        {
          action: 'CREATE',
          entity: 'Deal',
          payload: {
            unitId: brandPickerUnitId,
            brandId: { $ref: tempId },
            status: 'SIGNED',
            financials: { rent: { amount: 0, currency: 'EUR', basis: 'YEARLY' } },
            activateOnSign: true
          }
        }
      ], 'Create brand and assign to unit');

      toast.success(t.createBrandSuccess);
      hasLoadedAllBrandsRef.current = false;
      void hydrateAllBrands();
      void queryBrandsForPicker(brandSearch.trim(), false);
      void k.refreshInventory('Unit');
      handleCloseCreateBrand();
      handleCloseBrandPicker();
    } catch (error) {
      console.error('[Plan Mapper] Create brand failed', error);
      toast.error(t.createBrandFailed);
    } finally {
      toast.dismiss(toastId);
      setIsCreatingBrand(false);
    }
  }, [brandPickerUnitId, handleCloseBrandPicker, handleCloseCreateBrand, isCreatingBrand, kernel, newBrandName, t, hydrateAllBrands, queryBrandsForPicker, brandSearch]);

  const handleUseExistingBrand = useCallback(async (brandId: string) => {
    if (!brandPickerUnitId) return;
    await handleAssignBrand(brandPickerUnitId, brandId);
    handleCloseCreateBrand();
    handleCloseBrandPicker();
  }, [brandPickerUnitId, handleAssignBrand, handleCloseBrandPicker, handleCloseCreateBrand]);

  const brandPickerUnit = useMemo(
    () => (brandPickerUnitId ? unitRegistry[brandPickerUnitId] ?? null : null),
    [brandPickerUnitId, unitRegistry]
  );
  useEffect(() => {
    if (!isBrandPickerOpen) {
      brandPickerOpenRef.current = false;
      return;
    }
    if (brandPickerOpenRef.current) return;
    brandPickerOpenRef.current = true;
    const initialSearch = brandSearch.trim();
    void queryBrandsForPicker(initialSearch, false);
  }, [isBrandPickerOpen, brandSearch, queryBrandsForPicker]);

  useEffect(() => {
    if (!isBrandPickerOpen) return;
    const nextSearch = brandSearch.trim();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; the search fetch settles its own results, changing when it does is a behavioural change
    void queryBrandsForPicker(nextSearch, false);
  }, [brandSearch, isBrandPickerOpen, queryBrandsForPicker]);

  useEffect(() => {
    if (!isBrandPickerOpen) return;
    const container = brandListRef.current;
    const sentinel = brandLoadMoreRef.current;
    if (!container || !sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        if (!brandQueryPagination?.hasMore || isBrandQueryLoading) return;
        if (brandQuerySearchRef.current !== brandSearch.trim()) return;
        void queryBrandsForPicker(brandQuerySearchRef.current, true);
      },
      { root: container, rootMargin: '0px 0px 160px 0px', threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isBrandPickerOpen, brandQueryPagination?.hasMore, isBrandQueryLoading, brandSearch, queryBrandsForPicker]);

  const finalizeCalibration = useCallback(async (p1: NormalizedPoint, p2: NormalizedPoint) => {
    const k = kernel.current;
    if (!imgRef.current || !k) return;
    const floorEntity = k.getInventory().Floor.registry[floorId];
    if (!floorEntity) return;
    const aspectRatio = getAspectRatio(imgRef.current);
    const normDist = calculateNormalizedDistance(p1, p2, aspectRatio);

    const metersStr = window.prompt(t.distancePrompt, '1.0');
    const meters = parseFloat(metersStr || "");

    if (isNaN(meters) || meters <= 0) {
      setMapperCompat(prev => ({ ...prev, floorId: floorEntity.id, state: 'IDLE' }));
      return;
    }

    const newFactor = meters / normDist;

    try {
      await k.mutateInventory([{
        action: 'UPDATE',
        entity: 'Floor',
        id: floorEntity.id,
        payload: { calibrationFactor: newFactor }
      }], 'Update floor calibration');
      await k.syncEntity('Property', floorEntity.propertyId);
      toast.success(t.calibrationSet(newFactor.toFixed(1)));

      setMapperCompat(prev => ({ ...prev, state: 'IDLE', calibrationPoints: null }));
    } catch (e) {
      toast.error(t.saveCalibrationFailed);
    }
  }, [floorId, kernel, setMapperCompat, t]);

  const finalizeFrontageMeasurement = useCallback(async (points: NormalizedPoint[]) => {
    const k = kernel.current;
    if (!imgRef.current || !k) return;
    const inventory = k.getInventory();
    const floorEntity = inventory.Floor.registry[floorId];
    const activeUnitId = readMapper().activeUnitId;
    if (!floorEntity || !activeUnitId) return;

    if (!floorEntity.calibrationFactor || floorEntity.calibrationFactor <= 0) {
      toast.error(t.frontageNeedsCalibration);
      setMapperCompat(prev => ({ ...prev, state: 'IDLE', calibrationPoints: null, frontagePoints: [] }));
      return;
    }

    const cleanPoints = points.filter(Boolean);
    if (cleanPoints.length < 2) return;

    const aspectRatio = getAspectRatio(imgRef.current);
    const measuredMetersRaw = calculatePolylineNormalizedDistance(cleanPoints, aspectRatio) * floorEntity.calibrationFactor;
    const measuredMeters = Number(measuredMetersRaw.toFixed(2));
    if (!Number.isFinite(measuredMeters) || measuredMeters <= 0) {
      setMapperCompat(prev => ({ ...prev, state: 'IDLE', calibrationPoints: null, frontagePoints: [] }));
      return;
    }

    const frontageSegments = polylineToSegments(cleanPoints);

    const operations: any[] = [{
      action: 'UPDATE',
      entity: 'Unit',
      id: activeUnitId,
      payload: {
        frontage: measuredMeters,
      }
    }];

    const activeUnitPosition = inventory.Unit.registry[activeUnitId]?.positions?.find((position) => position.floorId === floorId);
    if (activeUnitPosition?.geoJson) {
      operations.push({
        action: 'UPDATE',
        entity: 'UnitPosition',
        payload: {
          unitId: activeUnitId,
          floorId,
          geoJson: {
            ...activeUnitPosition.geoJson,
            meta: {
              ...(activeUnitPosition.geoJson as any).meta,
              frontageSegment: frontageSegments[0] ?? null,
              frontageSegments,
              frontageMeters: measuredMeters,
            },
          },
        },
      });
    }

    try {
      await k.mutateInventory(operations, 'Measure unit frontage');
      toast.success(t.frontageSet(measuredMeters.toFixed(2)));
      void k.refreshInventory('Unit');
      setMapperCompat(prev => ({ ...prev, state: 'IDLE', calibrationPoints: null, frontagePoints: [] }));
    } catch (error) {
      toast.error(t.saveFrontageFailed);
    }
  }, [floorId, kernel, readMapper, setMapperCompat, t]);

  const handleCreateAndMapUnit = useCallback(async () => {
    const k = kernel.current;
    if (!k || !mapper.floorId) return;
    const targetFloorId = mapper.floorId;
    const inv = k.getInventory();
    const currentFloor = inv.Floor.registry[targetFloorId];
    if (!currentFloor) return;

    const allUnits = Object.values(inv.Unit.registry) as LeasingUnitDTO[];
    const existingIdentifiers = new Set(
      allUnits
        .filter(u => u.propertyId === currentFloor.propertyId)
        .map(u => (u.unitIdentifier ?? '').trim().toLowerCase())
        .filter(Boolean)
    );

    const base = `${currentFloor.level}-`;
    let counter = Math.max(1, allUnits.filter(u => u.floorIds?.includes(targetFloorId)).length + 1);
    let identifier = `${base}${counter}`;
    while (existingIdentifiers.has(identifier.toLowerCase())) {
      counter += 1;
      identifier = `${base}${counter}`;
      if (counter > 9999) break;
    }
    if (!identifier?.trim()) return;

    const ops = [
      {
        action: 'CREATE',
        entity: 'Unit',
          payload: {
            unitIdentifier: identifier.trim(),
            propertyId: currentFloor.propertyId,
            floorId: targetFloorId,
            type: 'RETAIL',
            status: 'AVAILABLE'
        }
      }
    ];

    try {
      const results = await k.mutateInventory(ops as any, 'Create and map unit');
      const unitResult = results.find((r: any) => r.entity === 'Unit');
      if (unitResult?.id) {
        toast.success(t.unitCreated(identifier));
        // Select it and start drawing
        setMapperCompat(prev => ({
          ...prev, 
          activeUnitId: unitResult.id,
          state: 'DRAWING_NEW',
          draftPoints: [],
          frontagePoints: [],
        }));
      }
    } catch (err: any) {
      toast.error(err?.message || t.creationFailed);
    }
  }, [kernel, mapper.floorId, t]);

  const handleImportUnitsFromFloor = useCallback(async (sourceFloorId: string) => {
    const k = kernel.current;
    if (!k || isImportingUnits || !mapper.floorId) return;
    const inv = k.getInventory();
    const currentFloor = inv.Floor.registry[mapper.floorId];
    const sourceFloor = inv.Floor.registry[sourceFloorId];
    if (!currentFloor || !sourceFloor) return;

    const sourceLabel = sourceFloor?.name ?? sourceFloor?.level?.toString() ?? 'Source floor';
    const allUnits = Object.values(inv.Unit.registry) as LeasingUnitDTO[];

    const sourceUnits = allUnits.filter(u => {
      const pos = u.positions?.find((p) => p.floorId === sourceFloorId);
      return !!pos?.geoJson?.coordinates && pos.geoJson.coordinates.length > 2;
    });

    if (sourceUnits.length === 0) {
      toast.info(t.importUnitsNone);
      return;
    }

    if (!window.confirm(t.importUnitsConfirm(sourceUnits.length, sourceLabel))) return;

    const existingIdentifiers = new Set(
      allUnits
        .filter(u => u.propertyId === currentFloor.propertyId)
        .map(u => (u.unitIdentifier ?? '').trim().toLowerCase())
        .filter(Boolean)
    );

    const makeUniqueIdentifier = (base: string) => {
      let candidate = base.trim();
      if (!candidate) candidate = 'Unit';
      let suffix = 1;
      while (existingIdentifiers.has(candidate.toLowerCase())) {
        candidate = `${base.trim() || 'Unit'}-${suffix}`;
        suffix += 1;
        if (suffix > 999) break;
      }
      existingIdentifiers.add(candidate.toLowerCase());
      return candidate;
    };

    const aspectRatio = imgRef.current ? getAspectRatio(imgRef.current) : null;
    const calibration = currentFloor.calibrationFactor || 1.0;

    let createdCount = 0;

    setIsImportingUnits(true);
    const toastId = toast.loading(`${t.importUnitsTitle} (0/${sourceUnits.length})`);

    try {
      for (let batchStart = 0; batchStart < sourceUnits.length; batchStart += IMPORT_BATCH_SIZE) {
        const batch = sourceUnits.slice(batchStart, batchStart + IMPORT_BATCH_SIZE);
        const operations: Array<{ action: string; entity: string; tempId?: string; payload?: any }> = [];

        batch.forEach((unit, idx) => {
          const globalIndex = batchStart + idx;
          const baseIdentifier = (unit.unitIdentifier ?? unit.name ?? `Unit ${globalIndex + 1}`).toString();
          const unitIdentifier = makeUniqueIdentifier(baseIdentifier);
          const tempId = `import-unit-${globalIndex}`;

          operations.push({
            action: 'CREATE',
            entity: 'Unit',
            tempId,
            payload: {
              unitIdentifier,
              propertyId: currentFloor.propertyId,
              floorId: mapper.floorId,
              type: unit.type ?? 'RETAIL',
              name: unit.name ?? null,
              areaSqm: unit.areaSqm ?? null,
              areaSqft: unit.areaSqm ? null : (unit.areaSqft ?? null),
              description: unit.description ?? null,
            }
          });

          const sourcePos = unit.positions?.find((p) => p.floorId === sourceFloorId);
          if (sourcePos?.geoJson?.coordinates?.length) {
            const coordinates = sourcePos.geoJson.coordinates;
            const geoJson = {
              ...sourcePos.geoJson,
              coordinates,
              meta: {
                ...(sourcePos.geoJson as any)?.meta,
                calculatedAreaSqm: aspectRatio ? calculateArea(coordinates, aspectRatio, calibration) : (sourcePos.geoJson as any)?.meta?.calculatedAreaSqm ?? null,
                calibrationSnapshot: calibration,
                centroid: getCentroid(coordinates),
              }
            };

            operations.push({
              action: 'UPDATE',
              entity: 'UnitPosition',
              payload: {
                unitId: { $ref: tempId },
                floorId: mapper.floorId,
                geoJson
              }
            });
          }

          createdCount += 1;
        });

        await k.mutateInventory(operations as any, 'Import units from floor');

        toast.loading(`${t.importUnitsTitle} (${createdCount}/${sourceUnits.length})`, { id: toastId });
      }

      toast.success(t.importUnitsSuccess(createdCount), { id: toastId });
      void k.refreshInventory('Unit');
      setIsImportUnitsOpen(false);
    } catch (err) {
      console.error('[Plan Mapper] Import units failed', err);
      toast.error(t.importUnitsFailed, { id: toastId });
    } finally {
      setIsImportingUnits(false);
    }
  }, [kernel, mapper.floorId, isImportingUnits, t]);

  // Coordinate conversion
  const getNormalizedCoords = useCallback((e: React.MouseEvent | MouseEvent): NormalizedPoint | null => {
    if (!imgRef.current) return null;
    const rect = imgRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    return { x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) };
  }, []);

  const getNormalizedCoordsStrict = useCallback((e: React.MouseEvent | MouseEvent): NormalizedPoint | null => {
    if (!imgRef.current) return null;
    const rect = imgRef.current.getBoundingClientRect();
    if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) {
      return null;
    }
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    return { x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) };
  }, []);

  // Snapping Logic
  const getSnappedPoint = useCallback((raw: NormalizedPoint): NormalizedPoint => {
    if (!imgRef.current) return raw;
    const rect = imgRef.current.getBoundingClientRect();
    const thresholdNormX = SNAP_THRESHOLD_PX / rect.width;
    const thresholdNormY = SNAP_THRESHOLD_PX / rect.height;

    let bestSnap: NormalizedPoint | null = null;
    let minSquaredDist = Infinity;
    const current = readMapper();

    // 1. Snap to existing vertices of OTHER units (Shared Walls)
    floorUnits.forEach(u => {
      // Don't snap to the unit we are currently modifying (avoids self-collapse)
      if (u.id === current.activeUnitId) return;

      const pos = u.positions?.find(p => p.floorId === floorId);
      if (pos?.geoJson?.coordinates) {
        pos.geoJson.coordinates.forEach(v => {
          const dx = Math.abs(raw.x - v.x);
          const dy = Math.abs(raw.y - v.y);
          
          if (dx < thresholdNormX && dy < thresholdNormY) {
            const dist = dx * dx + dy * dy;
            if (dist < minSquaredDist) {
              minSquaredDist = dist;
              bestSnap = v;
            }
          }
        });
      }
    });

    if (bestSnap) return bestSnap;

    // 2. Snap to current draft start point (ONLY when drawing new polygons)
    if (current.interactionMode === 'DRAWING_NEW' && current.draftPoints.length > 2) {
      const start = current.draftPoints[0];
      if (start && Math.abs(raw.x - start.x) < thresholdNormX && Math.abs(raw.y - start.y) < thresholdNormY) {
        return start;
      }
    }

    return raw;
  }, [floorId, floorUnits, readMapper]);

  // Load existing polygon into draft when unit is selected.
  // Important: do not force interaction mode here, otherwise tool changes can be overwritten.
  useEffect(() => {
    if (!mapper.activeUnitId || !mapper.floorId) return;
    const unit = unitRegistry[mapper.activeUnitId];
    const pos = unit?.positions?.find(p => p.floorId === mapper.floorId);

    setMapperCompat(prev => ({
      ...prev,
      draftPoints: pos?.geoJson?.coordinates ?? [],
    }));
  }, [mapper.activeUnitId, mapper.floorId, setMapperCompat, unitRegistry]);


  // Helper to center camera on a unit
  const handleUnitClick = useCallback((unitId: string) => {
    const unit = floorUnits.find(u => u.id === unitId);
    const pos = unit?.positions?.find(p => p.floorId === floorId);

    let nextViewport = viewportRef.current;

    // If unit has a polygon, center the camera on it
    if (pos?.geoJson?.coordinates && imgRef.current && containerRef.current) {
      const centroid = getCentroid(pos.geoJson.coordinates);
      const { clientWidth: imageWidth, clientHeight: imageHeight } = imgRef.current;
      const { clientWidth: containerWidth, clientHeight: containerHeight } = containerRef.current;
      const zoom = viewportRef.current.zoom;

      const targetX = containerWidth / 2 - centroid.x * imageWidth * zoom;
      const targetY = containerHeight / 2 - centroid.y * imageHeight * zoom;

      nextViewport = { ...viewportRef.current, x: targetX, y: targetY };
    }

    applyViewportChange(nextViewport);
    updateMapperState({
      activeUnitId: unitId,
      interactionMode: pos?.geoJson?.coordinates ? 'IDLE' : 'DRAWING_NEW',
      frontagePoints: [],
    });
  }, [applyViewportChange, floorId, floorUnits, updateMapperState]);

  const handleBulkPromote = useCallback(async () => {
    if (!mapper.floorId || !imgRef.current || !floor) return;

    const aspectRatio = getAspectRatio(imgRef.current);
    const calibration = floor.calibrationFactor || 1.0;

    const operations = floorUnits
      .filter(u => {
        const pos = u.positions?.find(p => p.floorId === mapper.floorId);
        const hasMapping = !!pos?.geoJson;
        const hasOfficialValue = (u.areaSqm ?? 0) > 0;
        return hasMapping && !hasOfficialValue;
      })
      .map(u => {
        const pos = u.positions!.find(p => p.floorId === mapper.floorId)!;
        
        // 1. Calculate area for THIS floor (using current calibration)
        const areaOnThisFloor = calculateArea(pos.geoJson!.coordinates, aspectRatio, calibration);
        
        // 2. Add areas from other floors (using their stored snapshots)
        let totalArea = areaOnThisFloor;
        u.positions?.forEach(p => {
          if (p.floorId !== mapper.floorId && p.geoJson?.meta?.calculatedAreaSqm) {
            totalArea += p.geoJson.meta.calculatedAreaSqm;
          }
        });

        return {
          action: 'UPDATE',
          entity: 'Unit',
          id: u.id,
          payload: { areaSqm: Math.round(totalArea) }
        };
      });

    if (operations.length === 0) {
      toast.info(t.noUnitsToPromote);
      return;
    }

    if (!window.confirm(t.confirmPromoteUnits(operations.length))) return;

    try {
      const k = kernel.current;
      if (!k) return;
      await k.mutateInventory(operations as any, 'Bulk promote unit areas');
      toast.success(t.bulkPromoteSuccess(operations.length));
      void k.refreshInventory('Unit');
    } catch (e) {
      toast.error(t.bulkPromoteFailed);
    }
  }, [mapper.floorId, floorUnits, floor, kernel, t]);

  const persistPolygon = useCallback(async (points: NormalizedPoint[]) => {
    const k = kernel.current;
    if (points.length < 3 || !imgRef.current || !k) return;
    const activeUnitId = readMapper().activeUnitId;
    const floorEntity = k.getInventory().Floor.registry[floorId];
    if (!activeUnitId || !floorEntity) return;

    const aspectRatio = getAspectRatio(imgRef.current);
    const calibration = floorEntity.calibrationFactor || 1.0;

    const manifest: SpatialManifest = {
      version: 1,
      type: 'Polygon',
      coordinates: points,
      meta: {
        calculatedAreaSqm: calculateArea(points, aspectRatio, calibration),
        calibrationSnapshot: calibration,
        centroid: getCentroid(points)
      }
    };

    try {
      await k.mutateInventory([{
        action: 'UPDATE',
        entity: 'UnitPosition',
        payload: {
          unitId: activeUnitId,
          floorId,
          geoJson: manifest
        }
      }], 'Persist floor polygon mapping');
      // Optimistic Update: We don't have a unitPositions map, but we can update the unit's metadata if needed.
      // For now, refreshing the units is the safest way to ensure the sidebar checkmark updates.
      void k.refreshInventory('Unit');
      toast.success(t.mappingSaved);
      
      setMapperCompat(prev => ({ ...prev, state: 'IDLE', draftPoints: [], activeUnitId: null, frontagePoints: [] }));
    } catch (e) {
      toast.error(t.saveMappingFailed);
    }
  }, [floorId, kernel, readMapper, setMapperCompat, t]);

  const persistPolygonForUnit = useCallback(async (
    unitId: string,
    points: NormalizedPoint[],
    messages?: { success?: string; error?: string }
  ) => {
    if (!mapper.floorId || points.length < 3 || !imgRef.current || !floor) return;
    const k = kernel.current;
    if (!k) return;

    const aspectRatio = getAspectRatio(imgRef.current);
    const calibration = floor.calibrationFactor || 1.0;

    const manifest: SpatialManifest = {
      version: 1,
      type: 'Polygon',
      coordinates: points,
      meta: {
        calculatedAreaSqm: calculateArea(points, aspectRatio, calibration),
        calibrationSnapshot: calibration,
        centroid: getCentroid(points)
      }
    };

    try {
      await k.mutateInventory([{
        action: 'UPDATE',
        entity: 'UnitPosition',
        payload: {
          unitId,
          floorId: mapper.floorId,
          geoJson: manifest
        }
      }], 'Persist unit polygon mapping');
      void k.refreshInventory('Unit');
      toast.success(messages?.success ?? t.mappingSaved);
      if (mapper.activeUnitId === unitId) {
        setMapperCompat(prev => ({ ...prev, draftPoints: points }));
      }
    } catch (e) {
      toast.error(messages?.error ?? t.saveMappingFailed);
    }
  }, [floor, kernel, mapper.activeUnitId, mapper.floorId, t]);

  const handleInflateMapping = useCallback(async (unitId: string) => {
    const k = kernel.current;
    if (!k) return;
    const unit = k.getInventory().Unit.registry[unitId];
    if (!unit || !mapper.floorId) return;
    const pos = unit.positions?.find((p) => p.floorId === mapper.floorId);
    const points = pos?.geoJson?.coordinates;
    if (!points || points.length < 3) {
      toast.info(t.inflateMappingMissing);
      return;
    }

    const inflated = inflatePolygon(points, 1.05);
    await persistPolygonForUnit(unitId, inflated, {
      success: t.inflateMappingSuccess,
      error: t.inflateMappingFailed
    });
  }, [kernel, mapper.floorId, persistPolygonForUnit, t]);

  const handleDeflateMapping = useCallback(async (unitId: string) => {
    const k = kernel.current;
    if (!k) return;
    const unit = k.getInventory().Unit.registry[unitId];
    if (!unit || !mapper.floorId) return;
    const pos = unit.positions?.find((p) => p.floorId === mapper.floorId);
    const points = pos?.geoJson?.coordinates;
    if (!points || points.length < 3) {
      toast.info(t.deflateMappingMissing);
      return;
    }

    const deflated = inflatePolygon(points, 0.95);
    await persistPolygonForUnit(unitId, deflated, {
      success: t.deflateMappingSuccess,
      error: t.deflateMappingFailed
    });
  }, [kernel, mapper.floorId, persistPolygonForUnit, t]);

  const handleUnitContextMenu = useCallback((e: React.MouseEvent, unitId: string) => {
    if (!kernel.current) return;
    e.preventDefault();
    e.stopPropagation();

    kernel.current.setMenuData({
      position: { x: e.clientX, y: e.clientY },
      actions: [
        { label: t.renameUnit, icon: <EditIcon size={14} />, onClick: () => {
          kernel.current?.setMenuData(null);
          void handleRenameUnit(unitId);
        }},
        { label: t.assignBrand, icon: <MemoSparkles size={14} />, onClick: () => {
          kernel.current?.setMenuData(null);
          handleOpenBrandPicker(unitId);
        }},
        { label: t.promoteToUnit, icon: <ArrowUpRight size={14} />, onClick: () => {
          kernel.current?.setMenuData(null);
          setMapperCompat(prev => ({ ...prev, activeUnitId: unitId }));
          void handlePromoteArea(unitId);
        }},
        { label: t.measureFrontage, icon: <Ruler size={14} />, onClick: () => {
          kernel.current?.setMenuData(null);
          setMapperCompat(prev => ({
            ...prev,
            activeUnitId: unitId,
            state: 'MEASURE_FRONTAGE',
            calibrationPoints: null,
            frontagePoints: [],
          }));
        }},
        { label: t.reinitializeMapping, icon: <MemoRotateCcw size={14} />, onClick: () => {
          kernel.current?.setMenuData(null);
          handleReinitializeMapping(unitId);
        }},
        { label: t.inflateMapping, icon: <Maximize2 size={14} />, onClick: () => {
          kernel.current?.setMenuData(null);
          void handleInflateMapping(unitId);
        }},
        { label: t.deflateMapping, icon: <Minimize2 size={14} />, onClick: () => {
          kernel.current?.setMenuData(null);
          void handleDeflateMapping(unitId);
        }},
        { label: t.deleteUnit, icon: <Trash2 size={14} />, onClick: () => {
          kernel.current?.setMenuData(null);
          void handleDeleteUnit(unitId);
        }},
      ]
    });
  }, [kernel, t, handleDeleteUnit, handleDeflateMapping, handleInflateMapping, handleOpenBrandPicker, handlePromoteArea, handleReinitializeMapping, handleRenameUnit]);

  const handleFinishDrawing = useCallback(async () => {
    const draftPoints = readMapper().draftPoints;
    if (draftPoints.length < 3) return;
    await persistPolygon(draftPoints);
  }, [persistPolygon, readMapper]);

  // Interaction Handlers
  // Declared after handleFinishDrawing/persistPolygon: handleDoubleClick lists them as deps.
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const raw = getNormalizedCoords(e);
    if (!raw) return;
    const current = readMapper();

    // Middle click or Space+Left click for Panning
    if (e.button === 1 || (e.button === 0 && current.interactionMode === 'PANNING')) {
      isDraggingRef.current = true;
      lastMousePosRef.current = { x: e.clientX, y: e.clientY };
      return;
    }

    if (e.button !== 0) return;
    const point = getSnappedPoint(raw);

    // Vertex Dragging Initiation
    if (current.interactionMode === 'IDLE' && current.draftPoints.length > 0) {
      if (!imgRef.current) return;
      const rect = imgRef.current.getBoundingClientRect();
      const thresholdX = SNAP_THRESHOLD_PX / rect.width;
      const thresholdY = SNAP_THRESHOLD_PX / rect.height;

      const idx = current.draftPoints.findIndex(p =>
        Math.abs(p.x - raw.x) < thresholdX && Math.abs(p.y - raw.y) < thresholdY
      );

      if (idx !== -1) {
        setMapperCompat(prev => ({ ...prev, draggingVertexIndex: idx, state: 'EDITING_POINT' }));
        return;
      }
    }

    // Implicit Pan in IDLE mode (if nothing else was clicked)
    if (current.interactionMode === 'IDLE') {
      isDraggingRef.current = true;
      lastMousePosRef.current = { x: e.clientX, y: e.clientY };
      return;
    }

    if (current.interactionMode === 'DRAWING_NEW') {
      if (e.detail > 1) return;
      setMapperCompat(prev => ({ ...prev, draftPoints: [...prev.draftPoints, point] }));
    } else if (current.interactionMode === 'CALIBRATING') {
      if (!current.calibrationPoints) {
        setMapperCompat(prev => ({ ...prev, calibrationPoints: [point, point] }));
      } else {
        finalizeCalibration(current.calibrationPoints[0], point);
      }
    } else if (current.interactionMode === 'MEASURE_FRONTAGE') {
      if (!current.activeUnitId) {
        toast.info(t.frontageSelectUnit);
        setMapperCompat(prev => ({ ...prev, state: 'IDLE', calibrationPoints: null, frontagePoints: [] }));
        return;
      }
      const calibrationFactor = kernel.current?.getInventory().Floor.registry[floorId]?.calibrationFactor;
      if (!calibrationFactor || calibrationFactor <= 0) {
        toast.error(t.frontageNeedsCalibration);
        setMapperCompat(prev => ({ ...prev, state: 'IDLE', calibrationPoints: null, frontagePoints: [] }));
        return;
      }
      setMapperCompat(prev => ({ ...prev, frontagePoints: [...prev.frontagePoints, point] }));
    }
  }, [finalizeCalibration, floorId, getNormalizedCoords, getSnappedPoint, kernel, readMapper, setMapperCompat, t]);

  const handleDoubleClick = useCallback(() => {
    const current = readMapper();
    if (current.interactionMode === 'DRAWING_NEW') {
      if (current.draftPoints.length < 3) return;
      void handleFinishDrawing();
      return;
    }

    if (current.interactionMode === 'MEASURE_FRONTAGE') {
      if (current.frontagePoints.length < 2) return;
      void finalizeFrontageMeasurement(current.frontagePoints);
    }
  }, [finalizeFrontageMeasurement, handleFinishDrawing, readMapper]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const raw = getNormalizedCoords(e);
    if (!raw) return;

    if (isDraggingRef.current) {
      const dx = e.clientX - lastMousePosRef.current.x;
      const dy = e.clientY - lastMousePosRef.current.y;
      applyViewportChange({
        ...viewportRef.current,
        x: viewportRef.current.x + dx,
        y: viewportRef.current.y + dy
      });
      lastMousePosRef.current = { x: e.clientX, y: e.clientY };
      return;
    }

    const current = readMapper();
    const snapped = getSnappedPoint(raw);
    const isSnapped = snapped !== raw;
    setMapperCompat(prev => ({ ...prev, snappedPoint: snapped, isSnapped }));

    if (current.interactionMode === 'EDITING_POINT' && draggingVertexIndexRef.current !== null) {
      setMapperCompat(prev => {
        const nextPoints = [...prev.draftPoints];
        nextPoints[prev.draggingVertexIndex!] = snapped;
        return { ...prev, draftPoints: nextPoints };
      });
    } else if (current.interactionMode === 'CALIBRATING' && current.calibrationPoints) {
      setMapperCompat(prev => ({
        ...prev,
        calibrationPoints: [prev.calibrationPoints![0], snapped]
      }));
    }
  }, [applyViewportChange, getNormalizedCoords, getSnappedPoint, readMapper, setMapperCompat]);

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false;
    if (readMapper().interactionMode === 'EDITING_POINT') {
      setMapperCompat(prev => ({ ...prev, state: 'IDLE', draggingVertexIndex: null }));
    }
  }, [readMapper, setMapperCompat]);

  // Le plan est deja telecharge une fois par le <img> du canvas, mais il est tainte
  // (redirection SAS cross-origin), donc l'auto-remplissage doit le redecoder depuis le
  // reseau. Sans cache, chaque clic "Remplir la forme" retelechargeait le blob Azure entier :
  // la route signe une SAS neuve a chaque appel, donc aucune reutilisation du cache HTTP.
  // On decode une seule fois par plan (doctrine MAINT_09).
  const loadPlanImageData = useCallback(async () => {
    const assetId = floor?.planFileId;
    if (!assetId) return null;

    const cached = planImageDataRef.current;
    if (cached && cached.assetId === assetId) return cached.promise;

    const promise = decodePlanImageData(assetId);
    planImageDataRef.current = { assetId, promise };

    // Un echec reseau ou un plan illisible ne doit pas geler l'auto-remplissage : on evince
    // l'entree pour que le clic suivant retente.
    const evict = () => {
      if (planImageDataRef.current?.promise === promise) planImageDataRef.current = null;
    };
    promise.then((result) => { if (!result) evict(); }, evict);

    return promise;
  }, [floor?.planFileId]);

  const extractPolygonFromPlan = useCallback(async (seed: NormalizedPoint): Promise<NormalizedPoint[] | null> => {
    const plan = await loadPlanImageData();
    if (!plan) return null;
    return extractPolygonFromImageData(seed, plan.imageData, plan.width, plan.height, fillSettings);
  }, [fillSettings, loadPlanImageData]);


  const handleFillFromPoint = useCallback(async (seed: NormalizedPoint) => {
    if (!mapper.activeUnitId) {
      toast.info(t.fillShapeSelectUnit);
      return;
    }
    if (isFillingShape) return;

    setIsFillingShape(true);
    const toastId = toast.loading(t.fillShapeLoading);

    try {
      const points = await extractPolygonFromPlan(seed);
      if (!points || points.length < 3) {
        toast.error(t.fillShapeFailed);
        return;
      }

      setMapperCompat(prev => ({ ...prev, draftPoints: points }));
      await persistPolygon(points);
    } catch (error) {
      console.error('[Plan Mapper] Fill shape failed', error);
      toast.error(t.fillShapeFailed);
    } finally {
      toast.dismiss(toastId);
      setIsFillingShape(false);
    }
  }, [extractPolygonFromPlan, isFillingShape, kernel, mapper.activeUnitId, persistPolygon, t]);

  const handleMapContextMenu = useCallback((e: React.MouseEvent) => {
    if (!kernel.current) return;
    const point = getNormalizedCoordsStrict(e);
    if (!point) return;

    e.preventDefault();
    e.stopPropagation();

    kernel.current.setMenuData({
      position: { x: e.clientX, y: e.clientY },
      actions: [
        {
          label: t.fillShape,
          icon: <Layers size={14} />,
          onClick: () => {
            kernel.current?.setMenuData(null);
            void handleFillFromPoint(point);
          }
        }
      ]
    });
  }, [getNormalizedCoordsStrict, handleFillFromPoint, kernel, t]);

  if (!floor || !floor.planFileId) return null;

  return (
    <div className="flex flex-col h-full w-full relative bg-[#0a0a0a] animate-in fade-in duration-300">
      {/* Top Bar */}
      <div className="h-16 border-b border-white/10 flex items-center justify-between px-6 bg-black/40 flex-none">
        <div className="flex items-center gap-4">
          <div className="p-2 bg-white/10 rounded-lg text-white">
            <Layers size={20} />
          </div>
          <div>
            <h2 className="text-sm font-bold text-white">{t.mapperTitle(floor.name)}</h2>
            <p className="text-[10px] text-white/40">{t.propertyLabel} {property?.name}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 bg-white/5 p-1 rounded-xl border border-white/10">
          <button 
            onClick={() => setMapperCompat(prev => ({ ...prev, state: 'IDLE', activeUnitId: null, draftPoints: [], frontagePoints: [] }))}
            className={clsx("p-2 rounded-lg transition-all", mapper.state === 'IDLE' ? "bg-white/10 text-white" : "text-white/40 hover:text-white")}
          >
            <MousePointer2 size={18} />
          </button>
          <button 
            disabled={!mapper.activeUnitId}
            onClick={() => setMapperCompat(prev => ({ ...prev, state: 'DRAWING_NEW', draftPoints: [], frontagePoints: [] }))}
            className={clsx("p-2 rounded-lg transition-all disabled:opacity-20", mapper.state === 'DRAWING_NEW' ? "bg-white text-black" : "text-white/40 hover:text-white")}
          >
            <PenTool size={18} />
          </button>
          <button 
            onClick={() => setMapperCompat(prev => ({ ...prev, state: 'CALIBRATING', calibrationPoints: null, frontagePoints: [] }))}
            className={clsx("p-2 rounded-lg transition-all", mapper.state === 'CALIBRATING' ? "bg-amber-500 text-white" : "text-white/40 hover:text-white")}
            title={t.calibrationHelp}
          >
            <Ruler size={18} />
          </button>
          <button
            disabled={!mapper.activeUnitId}
            onClick={() => {
              if (!mapper.activeUnitId) {
                toast.info(t.frontageSelectUnit);
                return;
              }
              setMapperCompat(prev => ({ ...prev, state: 'MEASURE_FRONTAGE', calibrationPoints: null, frontagePoints: [] }));
            }}
            className={clsx(
              "p-2 rounded-lg transition-all disabled:opacity-20",
              mapper.state === 'MEASURE_FRONTAGE'
                ? "bg-[#1E3563] text-[#93C5FD]"
                : "text-white/40 hover:text-white"
            )}
            title={t.measureFrontageTitle}
          >
            <ArrowUpRight size={18} />
          </button>
          <button
            onClick={() => setIsImportUnitsOpen(true)}
            className="p-2 rounded-lg transition-all text-white/40 hover:text-white"
            title={t.importUnitsTitle}
            disabled={importFloors.length === 0}
          >
            <Copy size={18} />
          </button>
          <button
            onClick={() => setIsFillSettingsOpen(true)}
            className="p-2 rounded-lg transition-all text-white/40 hover:text-white"
            title={t.fillSettingsTitle}
          >
            <SlidersHorizontal size={18} />
          </button>
        </div>

      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar: Units List */}
        <div className="w-80 border-r border-white/10 flex flex-col bg-black/20 h-full">
          <div className="flex-1 min-h-0 flex flex-col p-4 border-b border-white/10">
            <div className="text-[10px] text-white/40 font-bold mb-4 flex-none">{t.selectUnitToMap}</div>
            <button 
              onClick={handleCreateAndMapUnit}
              className="flex items-center gap-2 w-full px-3 py-2 mb-4 bg-white/5 hover:bg-white/10 text-white rounded-lg border border-white/10 transition-all text-[10px] font-bold"
            >
              <MemoPlus size={14} />
              <span>{t.createAndMapUnit}</span>
            </button>
            <div className="space-y-1 overflow-y-auto custom-scrollbar flex-1 pr-1">
              {floorUnits.map(u => {
                const isMapped = u.positions?.some(p => p.floorId === mapper.floorId && p.geoJson);
                const isSelected = mapper.activeUnitId === u.id;
                const occupant = unitOccupants[u.id];
                const logoFailed = !!(occupant?.logo && failedBrandLogos.has(occupant.logo));
                const fallbackLabel = occupant?.name || u.unitIdentifier;

                return (
                  <button
                    key={u.id}
                    onClick={() => handleUnitClick(u.id)}
                    onContextMenu={(e) => handleUnitContextMenu(e, u.id)}
                    className={clsx(
                      "w-full text-left px-3 py-2 rounded-lg text-xs transition-all flex items-center justify-between group border",
                      isSelected 
                        ? "bg-white/10 text-white border-white/20 shadow-lg" 
                        : isMapped 
                          ? "text-white/80 hover:bg-white/5 border-emerald-500/20 bg-emerald-500/5" 
                          : "text-white/40 hover:bg-white/5 border-transparent"
                    )}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <span className={clsx("truncate font-medium", isSelected ? "text-white" : isMapped ? "text-white/90" : "text-white/40")}>
                        {u.unitIdentifier}
                      </span>
                      
                      {/* Occupant Info */}
                      <div className="flex items-center gap-2 min-w-0 overflow-hidden">
                        {occupant?.logo && !logoFailed && (
                          <img
                            src={occupant.logo}
                            className="h-6 w-auto object-contain rounded-sm bg-white/5 shrink-0"
                            alt=""
                            onError={() => markBrandLogoFailed(occupant.logo)}
                          />
                        )}
                        {logoFailed ? (
                          <span className="text-[9px] text-white/60 truncate">{fallbackLabel}</span>
                        ) : occupant?.name ? (
                          <span className="text-[9px] text-white/50 truncate">{occupant.name}</span>
                        ) : null}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {isMapped ? (
                        <Check size={12} className="text-emerald-500" />
                      ) : (
                        <div className="w-1.5 h-1.5 rounded-full bg-white/10 group-hover:bg-white/30 transition-colors" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          
          <div className="p-4 border-t border-white/10 space-y-4 flex-none bg-black/40">
            {floor.calibrationFactor ? (
              <>
                <div className="flex items-center gap-3 text-emerald-400">
                  <Check size={16} />
                  <div className="text-[10px] font-bold">{t.scaleCalibrated}</div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center border-b border-white/5 pb-2">
                    <span className="text-[10px] text-white/40">{t.imageHeight}</span>
                    <span className="text-xs text-white font-mono">{floor.calibrationFactor.toFixed(1)} m</span>
                  </div>
                  <p className="text-[9px] text-white/30 leading-relaxed">
                    {t.surfaceAreaNote}
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3 text-amber-400">
                  <AlertCircle size={16} />
                  <div className="text-[10px] font-bold">{t.calibrationRequired}</div>
                </div>
                <p className="text-[10px] text-white/40 leading-relaxed">
                  {t.calibrationHelp}
                </p>
              </>
            )}
          </div>
        </div>

        {/* Main Canvas Area */}
        <div 
          ref={containerRef}
          className={clsx(
            "flex-1 relative overflow-hidden bg-[#0a0a0a]",
            mapper.state === 'PANNING' ? "cursor-grab" : 
            mapper.state === 'IDLE' ? "cursor-default" : "cursor-crosshair"
          )}
          style={{ 
            cursor: mapper.state === 'PANNING' ? 'grab' : 
                    mapper.state === 'IDLE' ? 'default' : 'crosshair' 
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onDoubleClick={handleDoubleClick}
          onContextMenu={handleMapContextMenu}
        >
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div 
              className="absolute pointer-events-auto"
              style={{
                transformOrigin: '0 0',
                transform: `translate(${mapperState.viewport.x}px, ${mapperState.viewport.y}px) scale(${mapperState.viewport.zoom})`,
                willChange: 'transform'
              }}
            >
              <img 
                key={floor.planFileId}
                ref={imgRef}
                src={`/api/inventory/assets/${floor.planFileId}`}
                alt="" 
                className="max-w-none shadow-2xl select-none"
                style={{ height: '70dvh' }}
                onLoad={(e) => {
                  setImgLoaded(true);
                  if (
                    viewportRef.current.x === 0 &&
                    viewportRef.current.y === 0 &&
                    viewportRef.current.zoom === 1
                  ) {
                    const container = containerRef.current;
                    if (container) {
                      const rect = container.getBoundingClientRect();
                      const imgRect = e.currentTarget.getBoundingClientRect();
                      applyViewportChange({
                        zoom: 1,
                        x: (rect.width - imgRect.width) / 2,
                        y: (rect.height - imgRect.height) / 2
                      });
                    }
                  }
                }}
                draggable={false}
                onDragStart={(e) => e.preventDefault()}
              />
              
              {/* SVG Overlay for Vectors */}
              {/* The overlay draws normalised (0..1) geometry in the plan's own pixel space, so it
                  needs the image's natural size, which exists only once the browser has decoded it.
                  The whole subtree is gated on `imgLoaded`, and the plan-change effect above puts
                  that flag back to false, so a new plan always re-renders after its own load rather
                  than reusing the previous image's dimensions. Reading the ref here is therefore
                  announced by state, which is the invariant the rule cannot see. */}
              {/* eslint-disable react-hooks/refs */}
              {imgLoaded && imgRef.current && (
                <svg
                  className="absolute inset-0 w-full h-full pointer-events-none"
                  viewBox={`0 0 ${imgRef.current.naturalWidth} ${imgRef.current.naturalHeight}`}
                  preserveAspectRatio="xMidYMid meet"
                >
                  {/* Passive Layer: Saved Unit Polygons */}
                  {floorUnits.map(unit => {
                    const pos = unit.positions?.find(p => p.floorId === mapper.floorId);
                    if (!pos?.geoJson?.coordinates) return null;

                    const isSelected = mapper.activeUnitId === unit.id;
                    const occupant = unitOccupants[unit.id];
                    
                    const baseHex = getUnitStatusHex(unit.status);
                    const alpha = opacityMode === 'TRANSPARENT' ? 0.4 : 0.85;
                    const statusColor = hexToRgba(baseHex, alpha);

                    const strokeColor = isSelected ? 'white' : 'rgba(255, 255, 255, 0.3)';

                    const centroid = pos.geoJson.meta?.centroid ?? getCentroid(pos.geoJson.coordinates);
                    const cx = centroid.x * imgRef.current!.naturalWidth;
                    const cy = centroid.y * imgRef.current!.naturalHeight;
                    
                    // Calculate Bounding Box in Image Pixels
                    let minX = 1, maxX = 0, minY = 1, maxY = 0;
                    pos.geoJson.coordinates.forEach(p => {
                      if (p.x < minX) minX = p.x;
                      if (p.x > maxX) maxX = p.x;
                      if (p.y < minY) minY = p.y;
                      if (p.y > maxY) maxY = p.y;
                    });

                    const pixelW = (maxX - minX) * imgRef.current!.naturalWidth;
                    const pixelH = (maxY - minY) * imgRef.current!.naturalHeight;
                    
                    // Use the smallest dimension to ensure the label fits inside the unit
                    const minDim = Math.min(pixelW, pixelH);
                    
                    // Proportional Sizing Factors
                    // Text: ~15% of the unit's narrowest side
                    // Logo: ~40% of the unit's narrowest side
                    const fontSize = Math.max(minDim * 0.15, 12 / mapper.viewport.zoom); // Min size fallback for readability
                    const logoSize = minDim * 0.4;
                    const spacing = minDim * 0.05;

                    // Don't render logo if it would be microscopic (< 5px)
                    const showLogo = occupant?.logo && logoSize > 5 && !failedBrandLogos.has(occupant?.logo);

                    return (
                      <g 
                        key={unit.id}
                        onMouseDown={(e) => {
                          if (mapper.state === 'IDLE') {
                            e.stopPropagation(); // Stop the click from reaching the canvas (which might trigger other actions)
                            setMapperCompat(prev => ({ ...prev, activeUnitId: unit.id }));
                          }
                        }}
                        // Enable pointer events ONLY in IDLE mode so we can select units.
                        // In DRAWING mode, we want clicks to pass through to the canvas to place points.
                        className={clsx(mapper.state === 'IDLE' ? "cursor-pointer pointer-events-auto" : "pointer-events-none")}
                      >
                        <polygon
                          points={pos.geoJson.coordinates.map(p => `${p.x * imgRef.current!.naturalWidth},${p.y * imgRef.current!.naturalHeight}`).join(' ')}
                          fill={statusColor}
                          stroke={strokeColor}
                          strokeWidth={(isSelected ? 2 : 1) / mapper.viewport.zoom}
                          vectorEffect="non-scaling-stroke"
                        />
                        
                        {/* Centroid Label Group */}
                        <g transform={`translate(${cx}, ${cy})`} className="pointer-events-none select-none">
                          {/* Unit Identifier */}
                          <text
                            y={showLogo ? -(logoSize / 2) - spacing : 0}
                            fill="white"
                            fontSize={fontSize}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontWeight="bold"
                            style={{ textShadow: '0 1px 3px rgba(0,0,0,0.9)' }}
                          >
                            {unit.unitIdentifier}
                          </text>
                          {/* Snapping Indicator */}
                          {mapper.snappedPoint && mapper.isSnapped && (
                            <circle 
                              cx={mapper.snappedPoint.x * (imgRef.current?.naturalWidth || 0)} 
                              cy={mapper.snappedPoint.y * (imgRef.current?.naturalHeight || 0)} 
                              r={8 / mapper.viewport.zoom} 
                              fill="none" 
                              stroke="white" 
                              strokeWidth={1.5 / mapper.viewport.zoom}
                              strokeDasharray="2,2"
                              vectorEffect="non-scaling-stroke"
                              className="pointer-events-none animate-pulse"
                            />
                          )}
                          {/* Occupant Logo */}
                          {showLogo && (
                            <image
                              href={occupant.logo!}
                              x={-logoSize / 2}
                              y={-(logoSize / 2) + fontSize * 0.6}
                              width={logoSize}
                              height={logoSize}
                              preserveAspectRatio="xMidYMid contain"
                              style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.8))' }}
                              onError={() => markBrandLogoFailed(occupant.logo)}
                            />
                          )}
                        </g>
                      </g>
                    );
                  })}
                  
                  {/* Calibration Line */}
                  {mapper.state === 'CALIBRATING' && mapper.calibrationPoints && (
                    <g>
                      <line
                        x1={mapper.calibrationPoints[0].x * imgRef.current.naturalWidth}
                        y1={mapper.calibrationPoints[0].y * imgRef.current.naturalHeight}
                        x2={mapper.calibrationPoints[1].x * imgRef.current.naturalWidth}
                        y2={mapper.calibrationPoints[1].y * imgRef.current.naturalHeight}
                        stroke="#f59e0b"
                        strokeWidth={1 / mapper.viewport.zoom}
                        vectorEffect="non-scaling-stroke"
                        strokeDasharray="4,4"
                      />
                      {[mapper.calibrationPoints[0], mapper.calibrationPoints[1]].map((p, i) => (
                        <g key={i} transform={`translate(${p.x * imgRef.current!.naturalWidth}, ${p.y * imgRef.current!.naturalHeight})`}>
                          <circle 
                            r={3 / mapper.viewport.zoom} 
                            fill="none" 
                            stroke="#f59e0b" 
                            strokeWidth={1 / mapper.viewport.zoom}
                            vectorEffect="non-scaling-stroke"
                          />
                          <circle 
                            r={0.5 / mapper.viewport.zoom} 
                            fill="#f59e0b" 
                            vectorEffect="non-scaling-stroke"
                          />
                        </g>
                      ))}
                    </g>
                  )}

                  {/* Frontage Segments */}
                  {(frontageSegmentsToRender.length > 0 || frontagePointsToRender.length > 0) && (
                    <g>
                      {frontageSegmentsToRender.map(([p1, p2], segmentIndex) => (
                        <line
                          key={`frontage-segment-${segmentIndex}`}
                          x1={p1.x * imgRef.current!.naturalWidth}
                          y1={p1.y * imgRef.current!.naturalHeight}
                          x2={p2.x * imgRef.current!.naturalWidth}
                          y2={p2.y * imgRef.current!.naturalHeight}
                          stroke="#38bdf8"
                          strokeWidth={4 / mapper.viewport.zoom}
                          vectorEffect="non-scaling-stroke"
                          strokeDasharray={`${Math.max(0.75, 6 / mapper.viewport.zoom)},${Math.max(0.75, 4 / mapper.viewport.zoom)}`}
                        />
                      ))}
                      {mapper.state === 'MEASURE_FRONTAGE' && mapper.snappedPoint && mapper.frontagePoints.length > 0 && (() => {
                        const anchor = mapper.frontagePoints[mapper.frontagePoints.length - 1];
                        if (!anchor) return null;
                        return (
                          <line
                            x1={anchor.x * imgRef.current!.naturalWidth}
                            y1={anchor.y * imgRef.current!.naturalHeight}
                            x2={mapper.snappedPoint.x * imgRef.current!.naturalWidth}
                            y2={mapper.snappedPoint.y * imgRef.current!.naturalHeight}
                            stroke="#7dd3fc"
                            strokeWidth={3 / mapper.viewport.zoom}
                            vectorEffect="non-scaling-stroke"
                            strokeDasharray={`${Math.max(0.75, 3 / mapper.viewport.zoom)},${Math.max(0.75, 3 / mapper.viewport.zoom)}`}
                            opacity={0.95}
                          />
                        );
                      })()}
                      {frontagePointsToRender.map((p, i) => (
                        <g key={`frontage-point-${i}`} transform={`translate(${p.x * imgRef.current!.naturalWidth}, ${p.y * imgRef.current!.naturalHeight})`}>
                          <circle
                            r={Math.max(3.2, 5.5 / mapper.viewport.zoom)}
                            fill="none"
                            stroke="#38bdf8"
                            strokeWidth={2 / mapper.viewport.zoom}
                            vectorEffect="non-scaling-stroke"
                          />
                          <circle
                            r={Math.max(0.9, 1.4 / mapper.viewport.zoom)}
                            fill="#38bdf8"
                            vectorEffect="non-scaling-stroke"
                          />
                        </g>
                      ))}
                    </g>
                  )}

                  {/* Current Draft Polygon */}
                  {mapper.draftPoints.length > 0 && (
                    <g>
                      <polyline
                        points={mapper.draftPoints.map(p => `${p.x * imgRef.current!.naturalWidth},${p.y * imgRef.current!.naturalHeight}`).join(' ')}
                        fill="rgba(255,255,255,0.05)"
                        stroke="white"
                        strokeWidth={1 / mapper.viewport.zoom}
                        vectorEffect="non-scaling-stroke"
                      />
                      {mapper.draftPoints.map((p, i) => {
                        const isDragging = mapper.draggingVertexIndex === i;
                        return (
                          <g key={i} transform={`translate(${p.x * imgRef.current!.naturalWidth}, ${p.y * imgRef.current!.naturalHeight})`}>
                            {/* Hitbox (Invisible but clickable) */}
                            <circle 
                              r={10 / mapper.viewport.zoom} 
                              fill="transparent" 
                              className={clsx(
                                mapper.state === 'IDLE' || mapper.state === 'EDITING_POINT'
                                  ? "pointer-events-auto cursor-grab active:cursor-grabbing"
                                  : "pointer-events-none"
                              )}
                            />
                            
                            {/* Visuals */}
                            {isDragging ? (
                              // DRAGGING STATE: "Sniper Scope" (Hollow, High Contrast, Thin Lines)
                              <g>
                                {/* Outer Ring (Cyan for visibility) */}
                                <circle 
                                  r={6 / mapper.viewport.zoom} 
                                  fill="none" 
                                  stroke="white" 
                                  strokeWidth={0.5 / mapper.viewport.zoom}
                                  vectorEffect="non-scaling-stroke"
                                  className="pointer-events-none"
                                />
                                {/* Crosshair Lines */}
                                <line x1={-8 / mapper.viewport.zoom} y1={0} x2={8 / mapper.viewport.zoom} y2={0} stroke="white" strokeWidth={0.5 / mapper.viewport.zoom} vectorEffect="non-scaling-stroke" />
                                <line x1={0} y1={-8 / mapper.viewport.zoom} x2={0} y2={8 / mapper.viewport.zoom} stroke="white" strokeWidth={0.5 / mapper.viewport.zoom} vectorEffect="non-scaling-stroke" />
                                {/* Tiny Center Dot */}
                                <circle 
                                  r={0.5 / mapper.viewport.zoom} 
                                  fill="white" 
                                  vectorEffect="non-scaling-stroke"
                                  className="pointer-events-none"
                                />
                              </g>
                            ) : (
                              // IDLE STATE: Standard White Ring
                              <g>
                                <circle 
                                  r={2.5 / mapper.viewport.zoom} 
                                  fill="none" 
                                  stroke="white" 
                                  strokeWidth={1 / mapper.viewport.zoom}
                                  vectorEffect="non-scaling-stroke"
                                  className="pointer-events-none"
                                />
                                <circle 
                                  r={0.5 / mapper.viewport.zoom} 
                                  fill="white" 
                                  vectorEffect="non-scaling-stroke"
                                  className="pointer-events-none"
                                />
                              </g>
                            )}
                          </g>
                        );
                      })}
                    </g>
                  )}
                </svg>
              )}
              {/* eslint-enable react-hooks/refs */}
            </div>
          </div>

          {/* Floating Controls */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-4 px-6 py-3 bg-black/60 backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl">
             <div className="flex items-center gap-2 border-r border-white/10 pr-4">
                <button 
                  onClick={() => setOpacityMode(prev => prev === 'TRANSPARENT' ? 'OPAQUE' : 'TRANSPARENT')}
                  className={clsx("p-2 transition-colors rounded-lg", opacityMode === 'OPAQUE' ? "bg-white/20 text-white" : "text-white/40 hover:text-white")}
                  title={t.toggleBlockPlanMode}
                >
                  <Layers size={18} />
                </button>
                <div className="w-px h-4 bg-white/10 mx-1" />
                <button onClick={() => adjustZoom(-0.2)} className="p-2 text-white/40 hover:text-white transition-colors"><ZoomOut size={18} /></button>
                <span className="text-[10px] font-mono text-white/60 w-12 text-center">{Math.round(mapper.viewport.zoom * 100)}%</span>
                <button onClick={() => adjustZoom(0.2)} className="p-2 text-white/40 hover:text-white transition-colors"><ZoomIn size={18} /></button>
             </div>
             
             <div className="flex items-center gap-4">
                <div className="flex flex-col border-r border-white/10 pr-4">
                  <span className="text-[9px] text-white/40 font-bold">
                    {mapper.state === 'MEASURE_FRONTAGE' ? t.measureFrontage : t.estimatedArea}
                  </span>
                  <span className="text-sm font-mono text-white">
                    {mapper.state === 'MEASURE_FRONTAGE'
                      ? `${measuredFrontagePreview.toFixed(2)} m`
                      // eslint-disable-next-line react-hooks/refs -- same invariant as the overlay above: the aspect ratio comes from the loaded plan image
                      : `${imgRef.current && floor ? calculateArea(mapper.draftPoints, getAspectRatio(imgRef.current), floor.calibrationFactor || 1.0).toFixed(1) : '0.0'} m²`}
                  </span>
                </div>
                <div className="flex items-center gap-3 border-r border-white/10 pr-4">
                  <button 
                    onClick={handleBulkPromote}
                    className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all text-[10px] font-bold border border-white/10"
                    title={t.promoteAllTitle}
                  >
                    <Layers size={14} />
                    <span>{t.promoteAll}</span>
                  </button>
                </div>
                {mapper.state === 'MEASURE_FRONTAGE' ? (
                  <button
                    disabled={mapper.frontagePoints.length < 2}
                    onClick={() => void finalizeFrontageMeasurement(mapper.frontagePoints)}
                    className="flex items-center gap-2 px-4 py-2 bg-[#1E3563] hover:bg-[#26457F] disabled:opacity-20 text-[#BFDBFE] rounded-xl transition-all text-xs font-bold shadow-lg"
                  >
                    <Ruler size={14} />
                    <span>{t.applyFrontage}</span>
                  </button>
                ) : (
                  <button 
                    disabled={mapper.draftPoints.length < 3}
                    onClick={handleFinishDrawing}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-20 text-white rounded-xl transition-all text-xs font-bold shadow-lg shadow-emerald-500/20"
                  >
                    <Save size={14} />
                    <span>{t.saveMapping}</span>
                  </button>
                )}
             </div>
          </div>
        </div>
      </div>

      {isBrandPickerOpen && (
        <div
          className="fixed inset-0 z-[150] bg-black/70 backdrop-blur-sm flex items-center justify-center"
          onClick={handleCloseBrandPicker}
        >
          <div
            className="w-[560px] max-w-[90vw] max-h-[80vh] bg-[#0b0f14] border border-white/10 rounded-xl shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/10 rounded-lg text-white">
                  <MemoSparkles size={16} />
                </div>
                <div>
                  <div className="text-xs text-white/60 font-bold">{t.brandPickerTitle}</div>
                  {brandPickerUnit && (
                    <div className="text-[10px] text-white/40">
                      {brandPickerUnit.unitIdentifier}
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={handleCloseBrandPicker}
                className="p-1.5 rounded text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                title={t.close || 'Close'}
              >
                <X size={16} />
              </button>
            </div>

            <div className="p-4 border-b border-white/10">
              <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
                <MemoSearch size={14} className="text-white/40" />
                <input
                  value={brandSearch}
                  onChange={(e) => setBrandSearch(e.target.value)}
                  placeholder={t.brandSearchPlaceholder}
                  className="w-full bg-transparent text-xs text-white placeholder:text-white/30 outline-none"
                />
              </div>
              <button
                onClick={handleOpenCreateBrand}
                className="mt-3 w-full px-3 py-2 rounded-lg border border-white/10 text-xs font-semibold text-white/70 bg-white/5 hover:bg-white/10 hover:text-white transition-colors"
              >
                {t.createBrand}
              </button>
            </div>

            <div ref={brandListRef} className="p-4 overflow-y-auto custom-scrollbar space-y-4">
              {brandPickerSections.matchedInTable.length > 0 && (
                <div>
                  <div className="text-[10px] text-white/40 mb-2">{t.brandSectionMatched}</div>
                  <div className="space-y-2">
                    {brandPickerSections.matchedInTable.map((brand) => (
                      <button
                        key={brand.id}
                        onClick={async () => {
                          if (!brandPickerUnitId) return;
                          await handleAssignBrand(brandPickerUnitId, brand.id);
                          handleCloseBrandPicker();
                        }}
                        className="w-full text-left px-3 py-2 rounded-lg text-xs bg-white/5 hover:bg-white/10 border border-white/10 text-white transition-colors"
                      >
                        {brand.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {brandPickerSections.tableOnly.length > 0 && (
                <div>
                  <div className="text-[10px] text-white/40 mb-2">{t.brandSectionTableOnly}</div>
                  <div className="space-y-2">
                    {brandPickerSections.tableOnly.map((brand) => (
                      <button
                        key={brand.id}
                        onClick={async () => {
                          if (!brandPickerUnitId) return;
                          await handleAssignBrand(brandPickerUnitId, brand.id);
                          handleCloseBrandPicker();
                        }}
                        className="w-full text-left px-3 py-2 rounded-lg text-xs bg-white/5 hover:bg-white/10 border border-white/10 text-white transition-colors"
                      >
                        {brand.name}
                      </button>
                    ))}
                    <div ref={brandLoadMoreRef} className="h-2" />
                  </div>
                </div>
              )}

              {brandPickerSections.matchedInTable.length === 0 &&
                brandPickerSections.tableOnly.length === 0 && (
                  <div className="text-xs text-white/40 italic">{t.noBrandResults}</div>
                )}

              {brandQueryPagination?.hasMore && (
                <button
                  onClick={() => void queryBrandsForPicker(brandQuerySearchRef.current, true)}
                  disabled={isBrandQueryLoading}
                  className={clsx(
                    "w-full h-9 rounded-lg border text-xs font-medium transition-colors",
                    isBrandQueryLoading
                      ? "border-white/10 text-white/40 bg-white/5 cursor-not-allowed"
                      : "border-white/10 text-white/70 bg-white/5 hover:bg-white/10 hover:text-white"
                  )}
                >
                  {isBrandQueryLoading ? 'Loading...' : 'Load more'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {isCreateBrandOpen && (
        <div
          className="fixed inset-0 z-[160] bg-black/70 backdrop-blur-sm flex items-center justify-center"
          onClick={handleCloseCreateBrand}
        >
          <div
            className="w-[520px] max-w-[92vw] bg-[#0b0f14] border border-white/10 rounded-xl shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/10 rounded-lg text-white">
                  <MemoPlus size={16} />
                </div>
                <div className="text-xs text-white/60 font-bold">{t.createBrandTitle}</div>
              </div>
              <button
                onClick={handleCloseCreateBrand}
                className="p-1.5 rounded text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                title={t.close || 'Close'}
              >
                <X size={16} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <label className="flex flex-col gap-2 text-xs text-white/70">
                <span className="text-[10px] text-white/40">{t.createBrandName}</span>
                <input
                  value={newBrandName}
                  onChange={(e) => setNewBrandName(e.target.value)}
                  placeholder={t.createBrandNamePlaceholder}
                  className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder:text-white/30 outline-none"
                />
              </label>

              {similarBrandMatches.length > 0 && (
                <div className="space-y-2">
                  <div className="text-[10px] text-white/40">{t.createBrandSimilar}</div>
                  <div className="space-y-2">
                    {similarBrandMatches.map(match => (
                      <div
                        key={match.id}
                        className="flex items-center justify-between px-3 py-2 rounded-lg border border-white/10 bg-white/5 text-xs text-white/80"
                      >
                        <span className="truncate">{match.name}</span>
                        <button
                          onClick={() => void handleUseExistingBrand(match.id)}
                          className="ml-3 px-2 py-1 text-[10px] font-semibold text-white/60 hover:text-white"
                        >
                          {t.createBrandUseExisting}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="px-4 py-3 border-t border-white/10 flex items-center justify-between">
              <button
                onClick={handleCloseCreateBrand}
                className="px-3 py-1.5 rounded-lg text-[10px] font-bold text-white/60 border border-white/10 hover:text-white hover:border-white/30"
              >
                {t.close || 'Close'}
              </button>
              <button
                onClick={handleCreateBrand}
                disabled={isCreatingBrand || !newBrandName.trim()}
                className={clsx(
                  "px-3 py-1.5 rounded-lg text-[10px] font-bold",
                  isCreatingBrand || !newBrandName.trim()
                    ? "bg-white/10 text-white/30 cursor-not-allowed"
                    : "bg-white text-black hover:bg-white/90"
                )}
              >
                {t.createBrandSubmit}
              </button>
            </div>
          </div>
        </div>
      )}

      {isFillSettingsOpen && (
        <div
          className="fixed inset-0 z-[150] bg-black/70 backdrop-blur-sm flex items-center justify-center"
          onClick={() => setIsFillSettingsOpen(false)}
        >
          <div
            className="w-[520px] max-w-[92vw] bg-[#0b0f14] border border-white/10 rounded-xl shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/10 rounded-lg text-white">
                  <SlidersHorizontal size={16} />
                </div>
                <div className="text-xs text-white/60 font-bold">{t.fillSettingsTitle}</div>
              </div>
              <button
                onClick={() => setIsFillSettingsOpen(false)}
                className="p-1.5 rounded text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                title={t.fillSettingsClose}
              >
                <X size={16} />
              </button>
            </div>

            <div className="p-4 grid grid-cols-2 gap-3 text-xs text-white/70">
              <label className="flex flex-col gap-1">
                <span className="text-[10px] text-white/40">{t.fillCannyLow}</span>
                <input
                  type="number"
                  min={0}
                  max={255}
                  value={fillSettings.cannyLow}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    if (!Number.isFinite(next)) return;
                    updateFillSetting('cannyLow', Math.max(0, Math.min(255, Math.round(next))));
                  }}
                  className="bg-black/30 border border-white/10 rounded-lg px-2 py-1 text-xs text-white"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] text-white/40">{t.fillCannyHigh}</span>
                <input
                  type="number"
                  min={0}
                  max={255}
                  value={fillSettings.cannyHigh}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    if (!Number.isFinite(next)) return;
                    updateFillSetting('cannyHigh', Math.max(0, Math.min(255, Math.round(next))));
                  }}
                  className="bg-black/30 border border-white/10 rounded-lg px-2 py-1 text-xs text-white"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] text-white/40">{t.fillLoDiff}</span>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={fillSettings.loDiff}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    if (!Number.isFinite(next)) return;
                    updateFillSetting('loDiff', Math.max(0, Math.min(100, Math.round(next))));
                  }}
                  className="bg-black/30 border border-white/10 rounded-lg px-2 py-1 text-xs text-white"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] text-white/40">{t.fillUpDiff}</span>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={fillSettings.upDiff}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    if (!Number.isFinite(next)) return;
                    updateFillSetting('upDiff', Math.max(0, Math.min(100, Math.round(next))));
                  }}
                  className="bg-black/30 border border-white/10 rounded-lg px-2 py-1 text-xs text-white"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] text-white/40">{t.fillMinArea}</span>
                <input
                  type="number"
                  min={0.001}
                  max={50}
                  step={0.1}
                  value={Math.round(fillSettings.minAreaRatio * 1000) / 10}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    if (!Number.isFinite(next)) return;
                    updateFillSetting('minAreaRatio', Math.max(0.00001, Math.min(0.5, next / 100)));
                  }}
                  className="bg-black/30 border border-white/10 rounded-lg px-2 py-1 text-xs text-white"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] text-white/40">{t.fillMaxArea}</span>
                <input
                  type="number"
                  min={1}
                  max={95}
                  step={1}
                  value={Math.round(fillSettings.maxAreaRatio * 100)}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    if (!Number.isFinite(next)) return;
                    updateFillSetting('maxAreaRatio', Math.max(0.05, Math.min(0.95, next / 100)));
                  }}
                  className="bg-black/30 border border-white/10 rounded-lg px-2 py-1 text-xs text-white"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] text-white/40">{t.fillBlurSize}</span>
                <input
                  type="number"
                  min={0}
                  max={11}
                  step={1}
                  value={fillSettings.blurSize}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    if (!Number.isFinite(next)) return;
                    let val = Math.max(0, Math.min(11, Math.round(next)));
                    if (val > 0 && val % 2 === 0) val += 1;
                    updateFillSetting('blurSize', val);
                  }}
                  className="bg-black/30 border border-white/10 rounded-lg px-2 py-1 text-xs text-white"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] text-white/40">{t.fillMorphIterations}</span>
                <input
                  type="number"
                  min={0}
                  max={6}
                  step={1}
                  value={fillSettings.morphIterations}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    if (!Number.isFinite(next)) return;
                    updateFillSetting('morphIterations', Math.max(0, Math.min(6, Math.round(next))));
                  }}
                  className="bg-black/30 border border-white/10 rounded-lg px-2 py-1 text-xs text-white"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] text-white/40">{t.fillDilateIterations}</span>
                <input
                  type="number"
                  min={0}
                  max={6}
                  step={1}
                  value={fillSettings.dilateIterations}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    if (!Number.isFinite(next)) return;
                    updateFillSetting('dilateIterations', Math.max(0, Math.min(6, Math.round(next))));
                  }}
                  className="bg-black/30 border border-white/10 rounded-lg px-2 py-1 text-xs text-white"
                />
              </label>
            </div>

            <div className="px-4 py-3 border-t border-white/10 flex items-center justify-between">
              <button
                onClick={() => setFillSettings({ ...FILL_DEFAULTS })}
                className="px-3 py-1.5 rounded-lg text-[10px] font-bold text-white/60 border border-white/10 hover:text-white hover:border-white/30"
              >
                {t.fillSettingsReset}
              </button>
              <button
                onClick={() => setIsFillSettingsOpen(false)}
                className="px-3 py-1.5 rounded-lg text-[10px] font-bold bg-white text-black"
              >
                {t.fillSettingsClose}
              </button>
            </div>
          </div>
        </div>
      )}

      {isImportUnitsOpen && (
        <div
          className="fixed inset-0 z-[150] bg-black/70 backdrop-blur-sm flex items-center justify-center"
          onClick={() => setIsImportUnitsOpen(false)}
        >
          <div
            className="w-[520px] max-w-[92vw] bg-[#0b0f14] border border-white/10 rounded-xl shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/10 rounded-lg text-white">
                  <Copy size={16} />
                </div>
                <div className="text-xs text-white/60 font-bold">{t.importUnitsTitle}</div>
              </div>
              <button
                onClick={() => setIsImportUnitsOpen(false)}
                className="p-1.5 rounded text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                title={t.close || 'Close'}
              >
                <X size={16} />
              </button>
            </div>

            <div className="p-4 space-y-2 max-h-[50vh] overflow-y-auto custom-scrollbar">
              {importFloors.length === 0 && (
                <div className="text-xs text-white/40 italic">{t.importUnitsEmpty}</div>
              )}
              {importFloors.map(f => (
                <button
                  key={f.id}
                  disabled={isImportingUnits}
                  onClick={() => void handleImportUnitsFromFloor(f.id)}
                  className={clsx(
                    "w-full text-left px-3 py-2 rounded-lg text-xs border transition-colors",
                    isImportingUnits
                      ? "border-white/5 text-white/30 bg-white/5 cursor-not-allowed"
                      : "border-white/10 text-white/80 bg-white/5 hover:bg-white/10"
                  )}
                >
                  <div className="font-medium">{f.name}</div>
                  <div className="text-[10px] text-white/40">{t.propertyLabel} {property?.name} · L{f.level}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};



